import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthProvider";
import { useTranslation } from "react-i18next";
import http from "../../api/apiClient";
import { createContactRequest } from "../../api/contactRequests";
import config from "../../config";
import Spinner from "../../components/Spinner";
import PPErrorPage from "../../components/pp-ErrorPage";
import useFormatCurrency from "../../utils/useFormatCurrency";
import { formatDateDMY } from "../../utils/dateUtils";
import { translateClassName } from "../../utils/classroomUtils";
import Modal from "../../components/Modal";
import PpFamilySelector from "./pp-FamilySelector";
import "../../styles/parent-portal.css";
import "./styles/parent-portal-mobile.css";
import "./styles/parent-portal-mobile-compact.css";
import "./styles/parent-portal-mobile-dark.css";
import ParentPortalResponsive from "./ParentPortalResponsive";
import ParentPortalDashboard from "./dashboard/ParentPortalDashboard";
import InfoTab from "./tabs/desktop/InfoTab";
import MobileInfoTab from "./info/InfoTab";
import MobileActionsTab from "./tabs/mobile/ActionsTab";
import MobileCalendarTab from "./tabs/mobile/CalendarTab";
import MobileFamilyTab from "./tabs/mobile/FamilyTab";
import MobileDirectoryTab from "./tabs/mobile/DirectoryTab";
import FamilyTab from "./tabs/desktop/FamilyTab";
import ChildrenTab from "./tabs/desktop/ChildrenTab";
import FinanceTab from "./tabs/desktop/FinanceTab";
import DirectoryTab from "./tabs/desktop/DirectoryTab";
import ActionsTab from "./tabs/desktop/ActionsTab";
import CalendarTab from "./tabs/desktop/CalendarTab";
import PreferencesTab from "./tabs/desktop/PreferencesTab";
import type { TripConsentFormState } from "./trips/TripConsentForm";
import SettingsTab from "./settings/SettingsTab";
import { buildFamilySituationMutationRequest } from "./utils/familySituationRequest";
import { createChildResourceLoader } from "./utils/childResourceLoader";
import { normalizePrivacyVisibility } from "../../features/privacy/definitions";
import { parentSchoolTasksApi } from "../../features/schoolTasks/api";
import ParentSchoolTasks from "./schoolTasks/ParentSchoolTasks";
import { useSchoolTasksVisibility } from "../../features/featureFlags";
import { ParentActionsView } from "./components/ParentActionsSubnav";
import ParentActionsViewCache from "./components/ParentActionsViewCache";
import PrivacyReviewModal, { needsPrivacyReview, shouldShowPrivacyReviewPopup } from "./privacy/PrivacyReviewModal";
import { isPrivacyReviewSnoozed, snoozePrivacyReview } from "./privacy/privacyReviewSnooze";
import { fetchChildHealthProfile } from "./family/healthProfileApi";

type TabKey = "overview" | "info" | "actions" | "family" | "children" | "finance" | "directory" | "preferences" | "calendar";

type FamilySituation =
  | "MARRIED"
  | "COHABITING"
  | "SEPARATED"
  | "DIVORCED"
  | "SINGLE_PARENT"
  | "WIDOWED"
  | "OTHER";

interface TabItem {
  key: TabKey;
  label: string;
}

type DirectoryMode = "parent" | "child";

type EntityId = string | number;

interface ModalState {
  show: boolean;
  type?: "success" | "error" | "info" | string;
  title?: string;
  message?: string;
}

interface ParentData {
  id: EntityId;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  newsletterOptOut?: boolean;
  newsletterStatus?: string;
  newsletterFirstSubscribedAt?: string | null;
  schoolSettings?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface ParentFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  zipCode: string;
  city: string;
  newsletterOptOut: boolean;
}

type ParentFormKey = keyof ParentFormState;

interface Family {
  id: EntityId;
  familyName?: string;
  familySituation?: FamilySituation | null;
  parent1Id?: EntityId;
  parent2Id?: EntityId;
  parent1FirstName?: string;
  parent1LastName?: string;
  parent2FirstName?: string;
  parent2LastName?: string;
  children?: Child[];
  [key: string]: unknown;
}

interface Child {
  id: EntityId;
  familyId?: EntityId | null;
  firstName?: string;
  lastName?: string;
  dateBirth?: string;
  dateRegistration?: string;
  gender?: string | null;
  bsn?: string | null;
  pendingBsn?: string | null;
  pending?: boolean;
  waiting?: boolean;
  withdrawnAt?: string | null;
  deceasedAt?: string | null;
  [key: string]: unknown;
}

interface DraftChildState {
  firstName: string;
  lastName: string;
  dateBirth: string;
  gender: string;
}

type PendingChild = Child & {
  rejected?: boolean;
  status?: string;
  action?: string;
  payloadData?: Partial<Child>;
  entityId?: EntityId | null;
};

interface ClassroomAssignment {
  id?: EntityId;
  uuid?: EntityId;
  classroomId?: EntityId;
  classroomUuid?: EntityId;
  classroom?: {
    id?: EntityId;
    uuid?: EntityId;
    className?: string;
    classroomName?: string;
    name?: string;
    label?: string;
  };
  className?: string;
  classroomName?: string;
  name?: string;
  label?: string;
  startDate?: string | null;
  endDate?: string | null;
  teacherFirstName?: string;
  teacherLastName?: string;
  teachers?: Array<{
    id?: EntityId;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    address?: string;
    zipCode?: string;
    city?: string;
  }>;
  isCurrent?: boolean;
  current?: boolean;
  [key: string]: unknown;
}

interface ClassroomState {
  loading?: boolean;
  error?: unknown;
  data?: ClassroomAssignment | ClassroomAssignment[] | null;
}

type ClassroomStateMap = Record<string | number, ClassroomState>;

const getClassroomName = (classroom?: ClassroomAssignment | null) => {
  const name =
    classroom?.className ||
    classroom?.classroomName ||
    classroom?.name ||
    classroom?.label ||
    classroom?.classroom?.className ||
    classroom?.classroom?.classroomName ||
    classroom?.classroom?.name ||
    classroom?.classroom?.label ||
    "";
  return typeof name === "string" ? name.trim() : "";
};

const stripTeacherTitle = (value?: unknown) =>
  typeof value === "string"
    ? value
        .trim()
        .replace(/^(juf|meester)\s+/i, "")
        .trim()
    : "";

const pickTeacherName = (classroom?: ClassroomAssignment | null) => {
  const item = classroom as any;
  const nested = item?.classroom || {};
  const team = Array.isArray(item?.teachers)
    ? item.teachers
        .map((teacher: any) => [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ").trim())
        .filter(Boolean)
    : [];
  if (team.length) return team.join(", ");
  const teacher =
    item?.teacher ||
    item?.primaryTeacher ||
    nested?.teacher ||
    nested?.primaryTeacher ||
    (Array.isArray(item?.teachers) ? item.teachers[0] : null) ||
    (Array.isArray(nested?.teachers) ? nested.teachers[0] : null);

  const firstName = stripTeacherTitle(
    item?.teacherFirstName ||
      nested?.teacherFirstName ||
      teacher?.firstName ||
      teacher?.givenName ||
      teacher?.firstname
  );
  if (firstName) return firstName;

  return stripTeacherTitle(
    item?.teacherName ||
      item?.teacherFullName ||
      nested?.teacherName ||
      nested?.teacherFullName ||
      teacher?.displayName ||
      teacher?.fullName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ")
  );
};

const trimClassroomNumber = (value: string) => value.replace(/\s+\d+\s*$/, "").trim();

const getClassroomCalendarLabel = (classroom?: ClassroomAssignment | null) => {
  const translated =
    translateClassName(classroom)?.trim() ||
    translateClassName(getClassroomName(classroom))?.trim() ||
    getClassroomName(classroom);
  const teacherName = pickTeacherName(classroom);

  if (teacherName && translated) return `${trimClassroomNumber(translated)} · ${teacherName}`;
  if (teacherName) return teacherName;
  return translated;
};

type PrivacyChannelKey =
  | "website"
  | "newsletter"
  | "socials"
  | "promotionalMaterials"
  | "internalPublications"
  | "localMedia"
  | "videoRecordings";

interface PrivacyConsent extends Partial<Record<PrivacyChannelKey, boolean>> {
  id?: EntityId;
  visibility?: "VISIBLE" | "NOT_VISIBLE" | "UNSET" | null;
  updatedAt?: string | null;
}

interface ChildPrivacyState {
  loading?: boolean;
  error?: unknown;
  data?: PrivacyConsent | null;
  expanded?: boolean;
  editing?: boolean;
}

type ChildPrivacyStateMap = Record<string | number, ChildPrivacyState>;

interface ChildInfoEditState {
  editing: boolean;
  draftBsn: string;
  saving?: boolean;
  error?: string;
  familyId?: EntityId | null;
}

type ChildInfoEditMap = Record<string | number, ChildInfoEditState>;

interface BankAccount {
  id: EntityId;
  iban: string;
  accountHolderName: string;
}

interface BankFormState {
  proposedIban: string;
  proposedAccountHolderName: string;
}

interface DirectoryEntry {
  id?: EntityId;
  parentId?: EntityId;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneMobile?: string;
  phoneOther?: string;
  addressStreet?: string;
  addressPostalCode?: string;
  addressCity?: string;
  notes?: string;
  published?: boolean;
  matchedChildren?: Array<{ name?: string }>;
  [key: string]: unknown;
}

interface DirectoryResponse {
  content: DirectoryEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

type DirectoryShareSettings = {
  displayName: boolean;
  email: boolean;
  phoneMobile: boolean;
  phoneOther: boolean;
  addressStreet: boolean;
  addressPostalCode: boolean;
  addressCity: boolean;
};

type DirectoryPreviewValues = {
  displayName: string;
  email: string;
  phoneMobile: string;
  phoneOther: string;
  addressStreet: string;
  addressPostalCode: string;
  addressCity: string;
};

interface DirectoryFormState extends DirectoryPreviewValues {
  published: boolean;
  notes: string;
}

interface MutationRequest {
  id: EntityId;
  entityType: string;
  action: string;
  entityId?: EntityId | null;
  status: string;
  requestedBy?: EntityId | string | null;
  payload?: string | null;
  createdAt?: string | null;
}

interface ParentMeetingInvitation {
  id: EntityId;
  token?: string | null;
  confirmationToken?: string | null;
  status: string;
  childId?: EntityId | null;
  childName?: string | null;
  classroomId?: EntityId | null;
  classroomName?: string | null;
  teacherName?: string | null;
  meetingType?: string | null;
  title?: string | null;
  description?: string | null;
  reservedUntil?: string | null;
  bookedSlotStart?: string | null;
  bookedSlotEnd?: string | null;
}

interface TripConsentRequest {
  id: EntityId;
  childId: EntityId;
  classroomId?: EntityId | null;
  classroomName?: string | null;
  requestedAt?: string | null;
  expiresAt?: string | null;
  parent1Id?: EntityId | null;
  parent2Id?: EntityId | null;
  parent1Phone?: string | null;
  parent2Phone?: string | null;
  parentPhoneNumbers?: string[] | null;
  parentPhones?: string | null;
  parent1ConsentedAt?: string | null;
  parent2ConsentedAt?: string | null;
  requiresDualConsent?: boolean;
  consentsRequired?: number | null;
  consentsGiven?: number | null;
  status?: string | null;
  childName?: string | null;
  label?: string | null;
  targetUrl?: string | null;
  prefill?: Record<string, unknown> | null;
}

interface ParentAction {
  type: string;
  label?: string | null;
  targetUrl?: string | null;
  childId?: EntityId | null;
  expiresAt?: string | null;
  classroomName?: string | null;
  status?: string | null;
}

interface SchoolYear {
  startDate: string;
  endDate: string;
  defaultMonthlyFee?: number | null;
}

interface ParentCalendarEvent {
  id?: EntityId;
  uuid?: EntityId;
  title?: string;
  description?: string | null;
  eventType?: string | null;
  scope?: "SCHOOL" | "CLASSROOM" | string | null;
  startDate?: string | number[] | null;
  endDate?: string | number[] | null;
  allDay?: boolean;
  location?: string | null;
  published?: boolean;
  classroomUuids?: EntityId[];
  classroomIds?: EntityId[];
  classroomNames?: string[];
  classrooms?: ClassroomAssignment[];
  schoolYearUuid?: EntityId | null;
  [key: string]: unknown;
}

const toChildKey = (childId: EntityId) => String(childId);

const hasPrivacyConsentRecord = (value: unknown): value is PrivacyConsent =>
  !!value && typeof value === "object" && !Array.isArray(value);

const PRIVACY_PREFERENCES_ACTION_TYPE = "PRIVACY_PREFERENCES";

const HEALTH_PROFILE_REVIEW_ACTION_TYPE = "HEALTH_PROFILE_REVIEW";

const isPrivacyPreferencesAction = (action: ParentAction) =>
  (action.type || "").toUpperCase() === PRIVACY_PREFERENCES_ACTION_TYPE;

const parseRequestPayload = (payload?: string | null) => {
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

const extractChildIdFromRequest = (req: MutationRequest) => {
  if (req.entityId != null) return String(req.entityId);
  const parsed = parseRequestPayload(req.payload);
  const parsedChild = parsed?.child ?? parsed?.entity ?? parsed;
  const candidate =
    parsedChild?.childId ??
    parsedChild?.id ??
    parsedChild?.entityId ??
    parsed?.childId ??
    parsed?.id;
  return candidate != null ? String(candidate) : null;
};

// component
const ParentPortal: React.FC = () => {
    const { keycloak, authenticated, token, logout } = useContext(AuthContext);
    const bearer = keycloak?.token ?? token ?? null;
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === "en" || i18n.language?.startsWith("en") ? "en-US" : "nl-NL";

    // Tabs
    const getInitialTab = (): TabKey => {
        if (typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches) {
            return "overview";
        }
        return "info";
    };

    const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab);
    const [actionsTabVisited, setActionsTabVisited] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    // Switched off in the backend: no tab, no section and no attention badge, whatever the permissions.
    const canViewSchoolTasks = useSchoolTasksVisibility().parentPortal;
    const [schoolTasksAttentionCount, setSchoolTasksAttentionCount] = useState<number | null>(null);
    const actionsView: ParentActionsView = searchParams.get("view") === "school-tasks" && canViewSchoolTasks ? "school-tasks" : "open";

    useEffect(() => {
        if (activeTab === "actions") setActionsTabVisited(true);
    }, [activeTab]);

    const setActionsView = useCallback((view: ParentActionsView) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", "actions");
        if (view === "school-tasks") next.set("view", "school-tasks");
        else next.delete("view");
        setSearchParams(next);
        setActiveTab("actions");
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!canViewSchoolTasks) {
            setSchoolTasksAttentionCount(null);
            return;
        }
        let active = true;
        const loadSchoolTaskAttention = async () => {
            try {
                const schoolYear = await parentSchoolTasksApi.schoolYear();
                const statuses = await parentSchoolTasksApi.statuses(schoolYear.id);
                if (!active) return;
                const count = statuses.reduce((total, status) => {
                    if (status.exempt) return total;
                    const parentTasks = Object.values(status.mandatoryParentTasks || {}).filter((complete) => !complete).length;
                    const generalOpen = Math.max(0, status.generalContributionRequiredCount - status.generalContributionCount);
                    return total + parentTasks + generalOpen;
                }, 0);
                setSchoolTasksAttentionCount(count);
            } catch {
                if (active) setSchoolTasksAttentionCount(null);
            }
        };
        void loadSchoolTaskAttention();
        return () => { active = false; };
    }, [canViewSchoolTasks]);

    // Parent Portal tabs (used in UI and URL sync)
    const desktopTabItems: TabItem[] = [
        { key: "overview", label: t("parentPortal.tabs.overview", { defaultValue: "Overview" }) },
        { key: "family", label: t("parentPortal.tabs.family", { defaultValue: "Family" }) },
        { key: "actions", label: t("parentPortal.tabs.actions", { defaultValue: "Actions" }) },
        { key: "calendar", label: t("parentPortal.tabs.calendar", { defaultValue: "Calendar" }) },
        { key: "finance", label: t("parentPortal.tabs.finance", { defaultValue: "Finances" }) },
        { key: "directory", label: t("parentPortal.tabs.directory", { defaultValue: "Directory" }) },
    ];

    const mobileTabItems: TabItem[] = [
        { key: "info", label: t("parentPortal.tabs.info", { defaultValue: "Information" }) },
        { key: "actions", label: t("parentPortal.tabs.actions", { defaultValue: "Actions" }) },
        { key: "family", label: t("parentPortal.tabs.family", { defaultValue: "Family" }) },
        { key: "children", label: t("parentPortal.tabs.children", { defaultValue: "Children" }) },
        { key: "calendar", label: t("parentPortal.tabs.calendar", { defaultValue: "Agenda" }) },
        { key: "finance", label: t("parentPortal.tabs.finance", { defaultValue: "Finance" }) },
        { key: "preferences", label: t("parentPortal.tabs.preferences", { defaultValue: "Preferences" }) },
        { key: "directory", label: t("parentPortal.tabs.directory", { defaultValue: "Directory" }) },
    ];

    const routableTabItems: TabItem[] = [
        ...desktopTabItems,
        ...mobileTabItems.filter((item) => !desktopTabItems.some((desktopItem) => desktopItem.key === item.key)),
    ];

    // Sync tab <-> URL query (before any early return)
    useEffect(() => {
        const qTab = searchParams.get("tab");
        if (qTab === "requests") {
            setActiveTab("actions");
            const next = new URLSearchParams(searchParams);
            next.set("tab", "actions");
            next.delete("view");
            setSearchParams(next, { replace: true });
            return;
        }
        if (qTab && qTab !== activeTab) {
            const match = routableTabItems.find((ti) => ti.key === qTab);
            if (match) {
                setActiveTab(match.key);
                return; // avoid immediate write-back
            }
        }
        if (!qTab) {
            const next = new URLSearchParams(searchParams);
            next.set("tab", activeTab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        const qTab = searchParams.get("tab");
        const hasStaleActionsView = activeTab !== "actions" && searchParams.has("view");
        if (qTab !== activeTab || hasStaleActionsView) {
            const next = new URLSearchParams(searchParams);
            next.set("tab", activeTab);
            if (activeTab !== "actions") next.delete("view");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const formatCurrency = useFormatCurrency() as (value: number) => string;
    const [children, setChildren] = useState<Child[]>([]);
    const [currentYearFee, setCurrentYearFee] = useState<number | null>(null);
    const [family, setFamily] = useState<Family | null>(null);
    const [familyEditMode, setFamilyEditMode] = useState(false);
    const [expandedChildId, setExpandedChildId] = useState<EntityId | null>(null);
    const [privacyActionChildId, setPrivacyActionChildId] = useState<EntityId | null>(null);
    const [bsnActionChildId, setBsnActionChildId] = useState<EntityId | null>(null);
    const [healthActionChildId, setHealthActionChildId] = useState<EntityId | null>(null);
    const [modal, setModal] = useState<ModalState>({ show: false, type: "", title: "", message: "" });
    const [familyDraft, setFamilyDraft] = useState<Family | null>(null);
    const [hasPendingFamilyUpdate, setHasPendingFamilyUpdate] = useState(false);
    const familySituationSubmissionRef = useRef(false);
    const [families, setFamilies] = useState<Family[]>([]);
    const [parentData, setParentData] = useState<ParentData | null>(null);
    const [privacyReviewSnoozed, setPrivacyReviewSnoozed] = useState<boolean | null>(null);
    const [hasPendingBankMutation, setHasPendingBankMutation] = useState(false);
    const [bsnSnoozes, setBsnSnoozes] = useState<Record<string, number>>({});
    const isDeceased = (c: Child | PendingChild) => !!c?.deceasedAt;
    const isWithdrawn = (c: Child | PendingChild) => !!c?.withdrawnAt && !isDeceased(c);
    const activeChildren = children.filter((c) => !isDeceased(c));
    const billableChildren = activeChildren.filter((c) => !isWithdrawn(c));

    useEffect(() => {
      if (parentData?.id == null) return;
      setPrivacyReviewSnoozed(isPrivacyReviewSnoozed(parentData.id));
    }, [parentData?.id]);
    // Per-kind privacy-cache edit-status
    const [privacyByChild, setPrivacyByChild] = useState<ChildPrivacyStateMap>({});
    const [childInfoExpanded, setChildInfoExpanded] = useState<Record<string | number, boolean>>({});
    const [childInfoEdit, setChildInfoEdit] = useState<ChildInfoEditMap>({});


    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);

    // Helpers

    const [classroomsByChild, setClassroomsByChild] = useState<ClassroomStateMap>({});
    const classroomLoaderRef = useRef<ReturnType<typeof createChildResourceLoader<ClassroomAssignment | ClassroomAssignment[]>> | null>(null);
    const privacyLoaderRef = useRef<ReturnType<typeof createChildResourceLoader<PrivacyConsent>> | null>(null);
    if (!classroomLoaderRef.current) {
      classroomLoaderRef.current = createChildResourceLoader<ClassroomAssignment | ClassroomAssignment[]>({
        setState: setClassroomsByChild,
        request: async (childId) => {
          const { data } = await http.get<ClassroomAssignment | ClassroomAssignment[]>(
            `/parent-portal/children/${childId}/current-class`
          );
          return data || null;
        },
        keyFor: toChildKey,
      });
    }
    if (!privacyLoaderRef.current) {
      privacyLoaderRef.current = createChildResourceLoader<PrivacyConsent>({
        setState: setPrivacyByChild,
        request: async (childId) => {
          const { data } = await http.get<PrivacyConsent>(`/parent-portal/children/${childId}/privacy-consent`);
          return hasPrivacyConsentRecord(data) ? data : null;
        },
        resolveError: (error) => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 404 || status === 204) {
            return null;
          }
          return undefined;
        },
        keyFor: toChildKey,
      });
    }

    // BSN snooze/dismiss (local only)
    const snoozeStorageKey = "pp_bsn_snooze";

    useEffect(() => {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem(snoozeStorageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setBsnSnoozes(parsed);
        }
      } catch {
        /* ignore */
      }
    }, []);

    useEffect(() => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(snoozeStorageKey, JSON.stringify(bsnSnoozes));
      } catch {
        /* ignore */
      }
    }, [bsnSnoozes]);

    const isBsnSnoozed = (childId: EntityId) => {
      const until = bsnSnoozes[String(childId)];
      return typeof until === "number" && until > Date.now();
    };

    const handleSnoozeBsn = (childId: EntityId, days = 7) => {
      const until = Date.now() + days * 24 * 60 * 60 * 1000;
      setBsnSnoozes((prev) => ({ ...prev, [String(childId)]: until }));
    };

    const handleDismissBsn = (childId: EntityId) => {
      const tenYears = 10 * 365 * 24 * 60 * 60 * 1000;
      const until = Date.now() + tenYears;
      setBsnSnoozes((prev) => ({ ...prev, [String(childId)]: until }));
    };

    const handleClearBsnSnoozes = () => setBsnSnoozes({});

    const loadClassroomsForChild = useCallback((childId: EntityId) => {
      return classroomLoaderRef.current?.load(childId) || Promise.resolve();
    }, []);

    useEffect(() => {
      if (!bearer || children.length === 0) return;

      children.forEach((child) => {
        if (child.id != null) void loadClassroomsForChild(child.id);
      });
    }, [bearer, children, loadClassroomsForChild]);

    const loadPrivacyFor = useCallback((childId: EntityId) => {
      return privacyLoaderRef.current?.load(childId) || Promise.resolve();
    }, []);

    const startEditPrivacy = (childId: EntityId) =>
      setPrivacyByChild(prev => {
        const key = toChildKey(childId);
        return { ...prev, [key]: { ...(prev[key]||{}), editing:true }};
      });

    const cancelEditPrivacy = (childId: EntityId) =>
      setPrivacyByChild(prev => {
        const key = toChildKey(childId);
        return { ...prev, [key]: { ...(prev[key]||{}), editing:false }};
      });

    const refreshParentAndChildData = async () => {
      try {
        const [parentResponse, familiesResponse] = await Promise.all([
          http.get<ParentData>("/parent-portal/me"),
          http.get<Family | Family[]>("/families/me"),
        ]);

        setParentData(parentResponse.data);

        const allFamilies = Array.isArray(familiesResponse.data)
          ? familiesResponse.data
          : [familiesResponse.data].filter(Boolean);
        setFamilies(allFamilies);

        const selectedFamily =
          allFamilies.find((item) => String(item.id) === String(family?.id)) ||
          (allFamilies.length === 1 ? allFamilies[0] : null);

        if (selectedFamily) {
          setFamily(selectedFamily);
          setFamilyDraft(selectedFamily);

          const response = await http.get<Child[]>(`/children/mine`, {
            params: { familyId: selectedFamily.id },
          });
          setChildren(response.data || []);
        }
      } catch (err) {
        console.error("Failed to refresh parent portal data after privacy save:", err);
      }
    };

    const savePrivacyFor = async (childId: EntityId, payload: PrivacyConsent) => {
      const normalizedVisibility = normalizePrivacyVisibility(payload?.visibility);
      const requestPayload = {
        ...payload,
        visibility: normalizedVisibility === "UNSET" ? null : normalizedVisibility,
      };
      const { data } = await http.put<PrivacyConsent>(`/parent-portal/children/${childId}/privacy-consent`, requestPayload);
      const key = toChildKey(childId);
      let normalizedData = hasPrivacyConsentRecord(data) ? data : null;

      if (!normalizedData) {
        try {
          const refreshed = await http.get<PrivacyConsent>(`/parent-portal/children/${childId}/privacy-consent`);
          normalizedData = hasPrivacyConsentRecord(refreshed.data) ? refreshed.data : null;
        } catch {
          normalizedData = null;
        }
      }

      setPrivacyByChild((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          loading: false,
          error: null,
          data: normalizedData,
          editing: false,
        },
      }));
      privacyLoaderRef.current?.markLoaded(childId);
      setParentActions((prev) =>
        prev.filter(
          (action) =>
            !isPrivacyPreferencesAction(action) ||
            String(action.childId ?? "") !== String(childId)
        )
      );
      await refreshParentAndChildData();
    };

    const togglePrivacyExpanded = (childId: EntityId) => {
      const key = toChildKey(childId);
      setPrivacyByChild((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          expanded: !prev[key]?.expanded,
        },
      }));
    };

    const toggleChildInfoExpanded = (childId: EntityId) => {
      const key = toChildKey(childId);
      setChildInfoExpanded((prev) => ({
        ...prev,
        [key]: !prev?.[key],
      }));
    };

    const startChildInfoEdit = (child: Child) => {
      if (pendingChildUpdateSet.has(String(child.id))) return;
      const key = toChildKey(child.id);
      setChildInfoExpanded((prev) => ({ ...prev, [key]: true }));
      setChildInfoEdit((prev) => ({
        ...prev,
        [key]: {
          editing: true,
          draftBsn: child.bsn || "",
          draftFirstName: child.firstName || "",
          draftLastName: child.lastName || "",
          saving: false,
          error: "",
          familyId: child.familyId || family?.id || null,
        },
      }));
    };

    const updateChildInfoDraft = (childId: EntityId, field: "bsn" | "firstName" | "lastName", value: string) => {
      const key = toChildKey(childId);
      setChildInfoEdit((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          draftBsn: field === "bsn" ? value : prev[key]?.draftBsn || "",
          draftFirstName: field === "firstName" ? value : prev[key]?.draftFirstName || "",
          draftLastName: field === "lastName" ? value : prev[key]?.draftLastName || "",
          error: "",
        },
      }));
    };

    const cancelChildInfoEdit = (childId: EntityId) => {
      const key = toChildKey(childId);
      setChildInfoEdit((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    };

    const submitChildInfoEdit = async (
      child: Child,
      overrideDraft?: Partial<{
        draftBsn: string;
        draftFirstName: string;
        draftLastName: string;
      }>,
      options?: { silent?: boolean }
    ) => {
      const key = toChildKey(child.id);
      const existingDraft = childInfoEdit[key];

      if (!existingDraft) return false;

      const draft = {
        ...existingDraft,
        ...(overrideDraft || {}),
      };

      const bsnEditable = !child.bsn;
      const newBsn = bsnEditable ? draft.draftBsn?.trim() || "" : child.bsn || "";
      const newFirst = draft.draftFirstName?.trim() ?? "";
      const newLast = draft.draftLastName?.trim() ?? "";

      const hasNameChange =
        newFirst !== (child.firstName || "") || newLast !== (child.lastName || "");

      const hasBsnChange =
        bsnEditable &&
        !!newBsn &&
        newBsn !== (child.bsn || "");

      if (!hasBsnChange && !hasNameChange) {
        setChildInfoEdit((prev) => ({
          ...prev,
          [key]: {
            ...draft,
            error: t("parentPortal.child_info.missing_changes", {
              defaultValue: "Enter a new name or BSN.",
            }),
          },
        }));
        return false;
      }

      setChildInfoEdit((prev) => ({
        ...prev,
        [key]: { ...draft, saving: true, error: "" },
      }));

      const mutationRequest = {
        entityType: "CHILD",
        action: "UPDATE",
        entityId: child.id,
        payload: {
          childId: child.id,
          familyId: draft.familyId || child.familyId || family?.id || null,
          ...(hasBsnChange ? { bsn: newBsn } : {}),
          ...(hasNameChange
            ? { firstName: newFirst || null, lastName: newLast || null }
            : {}),
        },
      };

      try {
        await http.post("/mutation-requests", mutationRequest);

        if (!options?.silent) {
          setModal({
            show: true,
            type: "success",
            title: t("common.modal.success"),
            message: t("parentPortal.request.messages.success"),
          });
        }

        setChildren((prev) =>
          prev.map((c) =>
            c.id === child.id
              ? {
                  ...c,
                  pendingBsn: hasBsnChange ? newBsn : c.pendingBsn,
                  firstName: hasNameChange ? newFirst || c.firstName : c.firstName,
                  lastName: hasNameChange ? newLast || c.lastName : c.lastName,
                }
              : c
          )
        );

        cancelChildInfoEdit(child.id);
        setChildInfoExpanded((prev) => ({ ...prev, [child.id]: true }));
        await fetchPendingRequests();

        return true;
      } catch (error) {
        console.error("Failed to submit child info update:", error);

        setModal({
          show: true,
          type: "error",
          title: t("common.modal.error"),
          message: t("parentPortal.request.messages.error"),
        });

        setChildInfoEdit((prev) => ({
          ...prev,
          [key]: { ...draft, saving: false },
        }));

        return false;
      }
    };

    const dirInputRef = useRef<HTMLInputElement | null>(null);

    const broadcastFamilyCount = useCallback((count: number) => {
        if (typeof window === "undefined") return;
        const normalized = Number.isFinite(count) ? count : 0;
        window.sessionStorage.setItem("parentPortalFamiliesCount", String(normalized));
        window.dispatchEvent(
            new CustomEvent("parentPortal:familiesLoaded", { detail: { count: normalized } })
        );
    }, []);

    useEffect(() => {
        broadcastFamilyCount(families.length);
    }, [families, broadcastFamilyCount]);
    // ---- Directory (Parent Portal) state ----
    const [dirTabBooted, setDirTabBooted] = useState(false);

    // Own profile
    const [myDirProfile, setMyDirProfile] = useState<DirectoryEntry | null>(null);
    const [myDirForm, setMyDirForm] = useState<DirectoryFormState>({
      published: false,
      displayName: "",
      email: "",
      phoneMobile: "",
      phoneOther: "",
      addressStreet: "",
      addressPostalCode: "",
      addressCity: "",
      notes: "",
    });
    const canViewDirectory = !!myDirProfile && !!myDirProfile.published;
    const [, setMyDirLoading] = useState<boolean>(false);
    const [myDirSaving, setMyDirSaving] = useState(false);
    const [, setMyDirMsg] = useState<string | null>(null);
    const [myDirErr, setMyDirErr] = useState<string | null>(null);
    

    // Directory search
    const [dirQ, setDirQ] = useState("");
    const [, setDirPage] = useState(0);
    const dirSize = 25;
    const [dirData, setDirData] = useState<DirectoryResponse | null>(null);
    const [dirLoading, setDirLoading] = useState(false);
    const [, setDirErr] = useState<string | null>(null);
    const [dirMode, setDirMode] = useState<DirectoryMode>("parent"); // 'parent' | 'child'

    // API helpers
    const loadMyDirectoryProfile = async (): Promise<DirectoryEntry | null> => {
      setMyDirLoading(true);
      setMyDirErr(null);
      try {
        const res = await http.get<DirectoryEntry | null>("/me/directory-profile");
        const data = res?.data ?? null; // 200 met body
        setMyDirProfile(data);
        setMyDirForm({
          published: !!data?.published,
          displayName: data?.displayName || "",
          email: data?.email || "",
          phoneMobile: data?.phoneMobile || "",
          phoneOther: data?.phoneOther || "",
          addressStreet: data?.addressStreet || "",
          addressPostalCode: data?.addressPostalCode || "",
          addressCity: data?.addressCity || "",
          notes: data?.notes || "",
        });
        return data;
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 204 || status === 404) {
          // ➜ Geen bestaand profiel: NIET als fout tonen
          setMyDirProfile(null);
          setMyDirForm({
            published: false,
            displayName: "",
            email: "",
            phoneMobile: "",
            phoneOther: "",
            addressStreet: "",
            addressPostalCode: "",
            addressCity: "",
            notes: "",
          });
          return null;
        }
        setMyDirErr(t("parentPortal.directory.load_profile_failed", { defaultValue: "Failed to load your directory profile." }));
        return null;
      } finally {
        setMyDirLoading(false);
      }
    };

    const saveMyDirectoryProfile = async () => {
      setMyDirSaving(true); setMyDirMsg(null); setMyDirErr(null);
      try {
        const payload = {
          published: !!myDirForm.published,
          displayName: myDirForm.displayName?.trim() || undefined,
          email: myDirForm.email?.trim() || undefined,
          phoneMobile: myDirForm.phoneMobile?.trim() || undefined,
          phoneOther: myDirForm.phoneOther?.trim() || undefined,
          addressStreet: myDirForm.addressStreet?.trim() || undefined,
          addressPostalCode: myDirForm.addressPostalCode?.trim() || undefined,
          addressCity: myDirForm.addressCity?.trim() || undefined,
          notes: myDirForm.notes?.trim() || undefined,
        };
        const { data } = await http.put("/me/directory-profile", payload);
        setMyDirProfile(data);
        setMyDirMsg(t("common.saved", { defaultValue: "Saved." }));
      } catch (e) {
        setMyDirErr(t("parentPortal.directory.save_failed", { defaultValue: "Failed to save." }));
      } finally {
        setMyDirSaving(false);
      }
    };

    const deleteMyDirectoryProfile = async () => {
      if (!window.confirm(t("parentPortal.directory.confirm_delete_profile", { defaultValue: "Delete your directory profile?" }))) return;
      setMyDirSaving(true); setMyDirErr(null); setMyDirMsg(null);
      try {
        await http.delete("/me/directory-profile");
        setMyDirProfile(null);
        setMyDirForm({
          published: false, displayName: "", email: "", phoneMobile: "", phoneOther: "",
          addressStreet: "", addressPostalCode: "", addressCity: "", notes: ""
        });
        setMyDirMsg(t("parentPortal.directory.profile_deleted", { defaultValue: "Profile deleted." }));
      } catch (e) {
        setMyDirErr(t("parentPortal.directory.delete_failed", { defaultValue: "Failed to delete." }));
      } finally {
        setMyDirSaving(false);
      }
    };

    const loadDirectory = async (page = 0, q = dirQ, mode: DirectoryMode = dirMode) => {
      setDirLoading(true); setDirErr(null);
      try {
        const { data } = await http.get<DirectoryResponse>("/directory", {
          params: { q, page, size: dirSize, by: mode } // <— belangrijke toevoeging
        });
        setDirData(data);
      } catch (e) {
        setDirErr("Failed to load directory.");
      } finally {
        setDirLoading(false);
      }
    };

    // --- Directory settings (geen vrije tekst, alleen kiezen wat je deelt) ---
    const [dirSettingsOpen, setDirSettingsOpen] = useState(false);
    const [share, setShare] = useState<DirectoryShareSettings>({
      displayName: true,
      email: true,
      phoneMobile: true,
      phoneOther: false,
      addressStreet: false,
      addressPostalCode: false,
      addressCity: false,
    });
    const [previewValues, setPreviewValues] = useState<DirectoryPreviewValues>({
      displayName: "",
      email: "",
      phoneMobile: "",
      phoneOther: "",
      addressStreet: "",
      addressPostalCode: "",
      addressCity: "",
    });

    const buildPreviewFromParent = (pd: ParentData | null = parentData): DirectoryPreviewValues => ({
      displayName: [pd?.firstName, pd?.lastName].filter(Boolean).join(" "),
      email: pd?.email || "",
      phoneMobile: pd?.phone || "",
      phoneOther: "",               // geen bron → leeg laten
      addressStreet: pd?.address || "",
      addressPostalCode: pd?.zipCode || "",
      addressCity: pd?.city || "",
    });

    // open settings: vul preview met parentData of bestaande directory-waarden
    const openDirSettings = () => {
      setMyDirErr(null);
      const seed = myDirProfile?.displayName
        ? {
            displayName: myDirProfile.displayName || "",
            email: myDirProfile.email || "",
            phoneMobile: myDirProfile.phoneMobile || "",
            phoneOther: myDirProfile.phoneOther || "",
            addressStreet: myDirProfile.addressStreet || "",
            addressPostalCode: myDirProfile.addressPostalCode || "",
            addressCity: myDirProfile.addressCity || "",
          }
        : buildPreviewFromParent(parentData);

      setPreviewValues(seed);

      // Standaard: “delen” aan voor niet-lege velden
      setShare({
        displayName: !!seed.displayName,
        email: !!seed.email,
        phoneMobile: !!seed.phoneMobile,
        phoneOther: !!seed.phoneOther,
        addressStreet: !!seed.addressStreet,
        addressPostalCode: !!seed.addressPostalCode,
        addressCity: !!seed.addressCity,
      });

      setDirSettingsOpen(true);
    };

    const saveDirectorySettings = async () => {
      // Bouw payload: alleen gedeelde velden meesturen; anders undefined (server wist/leeglaat)
      const pick = (k: keyof DirectoryShareSettings) =>
        share[k] ? previewValues[k] || undefined : undefined;

      setMyDirSaving(true); setMyDirErr(null); setMyDirMsg(null);
      try {
        const payload = {
          published: true, // “Set profile” publiceert meteen
          displayName: pick("displayName"),
          email: pick("email"),
          phoneMobile: pick("phoneMobile"),
          phoneOther: pick("phoneOther"),
          addressStreet: pick("addressStreet"),
          addressPostalCode: pick("addressPostalCode"),
          addressCity: pick("addressCity"),
          // notes weglaten: niet typbaar
        };
        const { data } = await http.put<DirectoryEntry>("/me/directory-profile", payload);
        setMyDirProfile(data);
        setMyDirForm({
          published: !!data?.published,
          displayName: data?.displayName || "",
          email: data?.email || "",
          phoneMobile: data?.phoneMobile || "",
          phoneOther: data?.phoneOther || "",
          addressStreet: data?.addressStreet || "",
          addressPostalCode: data?.addressPostalCode || "",
          addressCity: data?.addressCity || "",
          notes: data?.notes || "",
        });
        setMyDirMsg(t("common.saved", { defaultValue: "Saved." }));
        setDirSettingsOpen(false);
        // optioneel: direct eerste directory-pagina laden
        await loadDirectory(0, "");
      } catch (e) {
        setMyDirErr(t("parentPortal.directory.save_failed", { defaultValue: "Failed to save." }));
      } finally {
        setMyDirSaving(false);
      }
    };

    const turnOffListing = async () => {
      setMyDirSaving(true); setMyDirErr(null);
      try {
        const { data } = await http.put<DirectoryEntry>("/me/directory-profile", { published: false });
        setMyDirProfile(data);
        setDirSettingsOpen(false);        // 👈 sheet dicht
      } catch (e) {
        setMyDirErr(t("parentPortal.directory.save_failed", { defaultValue: "Failed to save." }));
      } finally {
        setMyDirSaving(false);
      }
    };

    const bootDoneRef = useRef<boolean>(false);

    // Component

    useEffect(() => {
       if (!authenticated && keycloak) keycloak.login();
    }, [authenticated, keycloak]);

    useEffect(() => {
    if (!authenticated || !bearer) return;
     if (bootDoneRef.current) return;        // voorkom herhaalde boot-loads (token refresh)

     (async () => {
       try {
         const response = await http.get<ParentData>("/parent-portal/me");
         setParentData(response.data);
         setError(null);
       } catch (err) {
         setError(t("parentPortal.load_failed"));
       } finally {
         setLoading(false);
         bootDoneRef.current = true;
       }
     })();
    }, [authenticated, bearer, t]);

    useEffect(() => {
        if (!parentData?.id || !bearer ) return;

        (async () => {
            try {
                const response = await http.get<Family | Family[]>("/families/me");

                const allFamilies = Array.isArray(response.data) ? response.data : [response.data];
                setFamilies(allFamilies);

                if (allFamilies.length === 1) {
                    setFamily(allFamilies[0]);
                    setFamilyDraft(allFamilies[0]);
                }
            } catch (err) {
                console.error("Failed to fetch families:", err);
                setFamilies([]);
            }
        })();
    }, [parentData, bearer]);

    useEffect(() => {
        if (!authenticated || !bearer ) return;

        (async () => {
            try {
                const response = await http.get<SchoolYear[]>("/years");

                const today = new Date();
                const matchingYear = response.data.find((year) => {
                    const start = new Date(year.startDate);
                    const end = new Date(year.endDate);
                    return today >= start && today <= end;
                });

                if (matchingYear?.defaultMonthlyFee) {
                    setCurrentYearFee(matchingYear.defaultMonthlyFee);
                }
            } catch (err) {
                console.error("Failed to fetch school year:", err);
            }
        })();
    }, [authenticated, bearer]);

    const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);

    useEffect(() => {
        if (!parentData?.id || !bearer ) return;

        (async () => {
            try {
                const res = await http.get<BankAccount>(`/bank-accounts/by-parent/${parentData.id}`);
                setBankAccount(res.data);
                setBankForm({
                    proposedIban: res.data?.iban || "",
                    proposedAccountHolderName: res.data?.accountHolderName || "",
                });
            } catch (err) {
                console.warn("Geen bankrekening gevonden voor ouder:", err?.response?.status);
            }
        })();
    }, [parentData, bearer]);

    // tab boot
    useEffect(() => {
      if (activeTab !== "directory" || dirTabBooted) return;

      (async () => {
        const initialQuery = dirQ.trim();

        await loadMyDirectoryProfile();
        setDirPage(0);

        if (initialQuery) {
          await loadDirectory(0, initialQuery, dirMode);
        } else {
          // Keep the normal empty directory state when the tab is opened directly.
          setDirData(null);
        }

        setDirTabBooted(true);

        window.requestAnimationFrame(() => {
          dirInputRef.current?.focus();
        });
      })();
    }, [activeTab, dirTabBooted, dirQ, dirMode]);

    const [editBankMode, setEditBankMode] = useState(false);
    const [bankForm, setBankForm] = useState<BankFormState>({
        proposedIban: bankAccount?.iban || "",
        proposedAccountHolderName: bankAccount?.accountHolderName || ""
    });

  const [pendingChildren, setPendingChildren] = useState<PendingChild[]>([]);
  const [pendingRequests, setPendingRequests] = useState<MutationRequest[]>([]);
  const [parentMeetingInvitations, setParentMeetingInvitations] = useState<ParentMeetingInvitation[]>([]);
  const [tripRequests, setTripRequests] = useState<TripConsentRequest[]>([]);
  const [tripRequestsLoading, setTripRequestsLoading] = useState(false);
  const [dismissedOutingChildIds, setDismissedOutingChildIds] = useState<Set<string>>(new Set());
  const [activeTripRequestId, setActiveTripRequestId] = useState<EntityId | null>(null);
  const [activeParentMeetingInvitationId, setActiveParentMeetingInvitationId] = useState<EntityId | null>(null);
  const [tripFormDraft, setTripFormDraft] = useState<TripConsentFormState | null>(null);
  const [tripFormSaving, setTripFormSaving] = useState(false);
  const [tripFormError, setTripFormError] = useState<string | null>(null);
  const [parentActions, setParentActions] = useState<ParentAction[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<ParentCalendarEvent[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarCopied, setCalendarCopied] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const withdrawnChildren = useMemo(
    () => (Array.isArray(children) ? children.filter((c) => c.withdrawnAt) : []),
    [children]
  );
  const activeTripRequest = useMemo(
    () =>
      activeTripRequestId
        ? tripRequests.find((req) => String(req.id) === String(activeTripRequestId)) || null
        : null,
    [activeTripRequestId, tripRequests]
  );

  const activeTripRequestWithCurrentParent = useMemo(
    () =>
      activeTripRequest
        ? {
            ...activeTripRequest,
            parent1Id: activeTripRequest.parent1Id || family?.parent1Id || null,
            parent2Id: activeTripRequest.parent2Id || family?.parent2Id || null,
            currentParentId: parentData?.id || null,
          }
        : null,
    [activeTripRequest, parentData?.id, family?.parent1Id, family?.parent2Id]
  );

  const activeParentMeetingInvitation = useMemo(
    () =>
      activeParentMeetingInvitationId
        ? parentMeetingInvitations.find((invitation) => String(invitation.id) === String(activeParentMeetingInvitationId)) ||
          null
        : null,
    [activeParentMeetingInvitationId, parentMeetingInvitations]
  );

    // Formulieren
    const [form, setForm] = useState<ParentFormState>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        zipCode: "",
        city: "",
        newsletterOptOut: false
    });

    // Add new child
    const [addMode, setAddMode] = useState(false);
    const [newChild, setNewChild] = useState<DraftChildState>({
        firstName: "",
        lastName: "",
        dateBirth: "",
        gender: ""
    });

    const handleNewChildChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const field = name as keyof DraftChildState;
        setNewChild((prev) => ({ ...prev, [field]: value }));
    };

    const handleCreateChild = async () => {
        if (!family?.id || !bearer ) return;

        const draftChild: Partial<Child> = {
            ...newChild,
            dateRegistration: new Date().toISOString().split("T")[0],
            familyId: family.id
        };

        const mutationRequest = {
            entityType: "CHILD",
            action: "CREATE",
            entityId: null,
            payload: draftChild
        };

        try {
            await http.post("/mutation-requests", mutationRequest);
            setChildren((prev) => [
                ...prev,
                {
                    id: `pending-${Date.now()}`,
                    ...draftChild,
                    pending: true
                }
            ]);
        } catch (error) {
            console.error("Failed to submit mutation request:", error);
            if (error?.response?.status === 409) {
                setModal({
                    show: true,
                    type: "error",
                    title: t("common.modal.error"),
                    message: t("parentPortal.duplicate_child_error")
                });
            } else {
                setModal({
                    show: true,
                    type: "error",
                    title: t("common.modal.error"),
                    message: t("parentPortal.create_child_failed")
                });
            }
        } finally {
            setAddMode(false);
            setNewChild({ firstName: "", lastName: "", dateBirth: "", gender: "" });
        }
    };

    const handleSubmitBankChange = async () => {
        if (!bearer || !parentData?.id) return;

        // Blokkeer dubbele IBAN-mutaties vanuit ouderportaal
        if (hasPendingBankMutation) {
            setModal({
                show: true,
                type: "error",
                title: t("common.modal.error"),
                message: t("parentPortal.bank_change_pending", {
                    defaultValue: "Your new bank account number has been saved and is ready for administrative processing."
                })
            });
            return;
        }

        try {
            if (!bankAccount) {
                const mutationRequest = {
                    entityType: "BANK_ACCOUNT",
                    action: "CREATE",
                    entityId: null,
                    payload: {
                        iban: bankForm.proposedIban,
                        accountHolderName: bankForm.proposedAccountHolderName,
                        parentUuid: parentData.id
                    }
                };

                await http.post("/mutation-requests", mutationRequest);
            } else {
                const mutationRequest = {
                    entityType: "BANK_ACCOUNT",
                    action: "UPDATE",
                    entityId: bankAccount.id,
                    payload: {
                        proposedIban: bankForm.proposedIban,
                        proposedAccountHolderName: bankForm.proposedAccountHolderName
                    }
                };

                await http.post("/mutation-requests", mutationRequest);
            }

            setEditBankMode(false);
            setModal({
                show: true,
                type: "success",
                title: t("common.modal.success"),
                message: t("parentPortal.bank_change_success", { defaultValue: "Change submitted" })
            });

            await fetchPendingRequests();
        } catch (err) {
            console.error("Fout bij wijzigen of aanmaken rekening:", err);
            setModal({
                show: true,
                type: "error",
                title: t("common.modal.error"),
                message: t("parentPortal.request.messages.error")
            });
        }
    };

    const fetchPendingRequests = async () => {
      try {
        const response = await http.get<MutationRequest[]>("/mutation-requests/my-requests", { params: { includeParent: true }});
        const requests = response.data || [];
        setPendingRequests(requests);

        const parsedChildren: PendingChild[] = requests
          .filter((req) => req.entityType === "CHILD" && req.status !== "APPROVED")
          .map((req) => {
            const parsed = JSON.parse(req.payload || "{}") as Partial<Child>;
            return {
              ...parsed,
              pending: req.status === "PENDING",
              rejected: req.status === "REJECTED",
              status: req.status,
              action: req.action,
              payloadData: parsed,
              id: parsed.id || req.id,
              entityId: req.entityId,
              familyId: parsed.familyId,
            };
          });

        setPendingChildren(parsedChildren);

        setChildren((prev) => {
          if (!prev.length) return prev;
          let changed = false;
          const updated = prev.map((child) => {
            const pendingMatch = parsedChildren.find(
              (pending) =>
                (pending.entityId && pending.entityId === child.id) ||
                pending.id === child.id
            );

            const pendingBsnValue = child.pendingBsn || pendingMatch?.bsn || pendingMatch?.payloadData?.bsn || null;
            const hasPendingBsn = Boolean(
              pendingMatch &&
                pendingMatch.action === "UPDATE" &&
                (pendingMatch.status === "PENDING" || pendingMatch.pending) &&
                pendingBsnValue
            );

            if (hasPendingBsn) {
              if (child.pendingBsn !== pendingBsnValue) {
                changed = true;
                return { ...child, pendingBsn: pendingBsnValue };
              }
              return child;
            }

            if (child.pendingBsn) {
              const { pendingBsn, ...rest } = child;
              changed = true;
              return rest;
            }

            return child;
          });

          return changed ? updated : prev;
        });
        } catch (error) {
            console.error("Failed to fetch pending requests:", error);
        }
    };

    const fetchParentMeetingInvitations = async () => {
      try {
        const response = await http.get<ParentMeetingInvitation[]>("/parent-meetings/me/invitations");
        const invitations = Array.isArray(response.data) ? response.data : [];
        setParentMeetingInvitations(invitations);
        setActiveParentMeetingInvitationId((current) => {
          if (!current) return current;
          const stillOpen = invitations.some(
            (invitation) =>
              String(invitation.id) === String(current) &&
              ["INVITED", "RESERVED"].includes((invitation.status || "").toUpperCase())
          );
          return stillOpen ? current : null;
        });
      } catch (error) {
        console.error("Failed to fetch parent meeting invitations:", error);
        setParentMeetingInvitations([]);
      }
    };

    useEffect(() => {
        if (bearer) {
            fetchPendingRequests();
            fetchParentMeetingInvitations();
        }
    }, [bearer]);

    useEffect(() => {
        if (!parentData?.id || pendingRequests.length === 0) return;

        const match = pendingRequests.some(
            (req) =>
                req.entityType === "BANK_ACCOUNT" &&
                (req.action === "CREATE" || req.action === "UPDATE") &&
                req.status === "PENDING" &&
                req.requestedBy?.toString() === parentData.id?.toString()
        );

        setHasPendingBankMutation(match);
    }, [parentData, pendingRequests]);

    // Vul formulier met parentData zodra het beschikbaar is
    useEffect(() => {
        if (parentData) {
            setForm({
                firstName: parentData.firstName || "",
                lastName: parentData.lastName || "",
                email: parentData.email || "",
                phone: parentData.phone || "",
                address: parentData.address || "",
                zipCode: parentData.zipCode || "",
                city: parentData.city || "",
                newsletterOptOut: !!parentData.newsletterOptOut
            });
        }
    }, [parentData]);

    useEffect(() => {
        if (!family?.id || !bearer ) return;

        (async () => {
            try {
                const response = await http.get<Child[]>(`/children/mine`, { params: { familyId: family.id }});
                setChildren(response.data || []);
            } catch (err) {
                console.error("Failed to fetch children for selected family:", err);
            }
        })();
    }, [family, bearer]);

    const handleSaveFamilySituation = async () => {
        if (!family?.id || !bearer || familyDraft == null || hasPendingFamilyUpdate || familySituationSubmissionRef.current) return;

        if (family.familySituation === familyDraft.familySituation) {
            setFamilyEditMode(false);
            return;
        }

        const mutationRequest = buildFamilySituationMutationRequest(family.id, familyDraft.familySituation);

        familySituationSubmissionRef.current = true;
        try {
            await http.post("/mutation-requests", mutationRequest);
            setModal({
                show: true,
                type: "success",
                title: t("common.modal.success"),
                message: t("parentPortal.family_situation_change_requested", { defaultValue: "We’ve received your update. We’ll process the change and notify you." })
            });
            setFamilyEditMode(false);
            setHasPendingFamilyUpdate(true);
        } catch (error) {
            console.error("Failed to submit family situation request:", error);
            setModal({
                show: true,
                type: "error",
                title: t("common.modal.error"),
                message: t("parentPortal.family_situation_change_failed", { defaultValue: "We could not submit your change right now. Please try again." })
            });
        } finally {
            familySituationSubmissionRef.current = false;
        }
    };

    const handleSubmitFamilySituationRequest = async ({
        reason,
        note,
    }: {
        reason: string;
        note: string;
    }) => {
        if (!family?.id || !bearer) return false;

        try {
            await createContactRequest({
                category: "FAMILY_SITUATION",
                familyId: family.id,
                reason,
                note,
                contactRequested: true,
            });
            return true;
        } catch (error) {
            console.error("Failed to submit family situation contact request:", error);
            setModal({
                show: true,
                type: "error",
                title: t("common.modal.error"),
                message: t("parentPortal.request.messages.error", {
                    defaultValue: "Request failed. Please try again.",
                }),
            });
            return false;
        }
    };

    const handleSubmitTeacherContactRequest = async (payload: {
        childId?: EntityId | null;
        classroomId?: EntityId | null;
        teacherId?: EntityId | null;
        subject: string;
        note: string;
        preferredContactMethod: string;
    }) => {
        if (!payload?.childId || !bearer) return false;

        try {
            await createContactRequest({
                category: "TEACHER_CALLBACK",
                childId: payload.childId,
                classroomId: payload.classroomId || null,
                teacherId: payload.teacherId || null,
                subject: payload.subject,
                note: payload.note,
                preferredContactMethod: payload.preferredContactMethod,
            });
            return true;
        } catch (error) {
            console.error("Failed to submit teacher contact request:", error);
            setModal({
                show: true,
                type: "error",
                title: t("common.modal.error"),
                message: t("parentPortal.request.messages.error", {
                    defaultValue: "Request failed. Please try again.",
                }),
            });
            return false;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, type, checked, value } = e.target;
        const field = name as ParentFormKey;
        const nextValue = (type === "checkbox" ? checked : value) as ParentFormState[ParentFormKey];
        setForm((prev) => ({ ...prev, [field]: nextValue }));
    };

    const handleSave = async () => {
        try {
            const payload = {
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone,
                address: form.address,
                zipCode: form.zipCode,
                city: form.city,
                newsletterOptOut: !!form.newsletterOptOut,
            };

            await http.put("/parent-portal/me", payload);
            const { data } = await http.get<ParentData>("/parent-portal/me");
            setParentData(data);
            setEditMode(false);
        } catch (error) {
            setModal({
              show: true,
              type: "error",
              title: t("common.modal.error", { defaultValue: "Error" }),
              message: t("common.save_error", { defaultValue: "Failed to save changes." }),
            });
            console.error(error);
        }
    };

    useEffect(() => {
        if (!family?.id) return;

        const isPending = pendingRequests.some(
            (req) => {
                if (
                    req.entityType !== "FAMILY" ||
                    String(req.entityId || "") !== String(family.id) ||
                    req.action !== "UPDATE" ||
                    req.status !== "PENDING"
                ) {
                    return false;
                }

                const payload = parseRequestPayload(req.payload);
                return Object.prototype.hasOwnProperty.call(payload || {}, "familySituation");
            }
        );

        setHasPendingFamilyUpdate(isPending);
    }, [pendingRequests, family]);

    const pendingChildUpdateSet = useMemo(() => {
      return new Set(
        pendingRequests
          .filter(
            (req) =>
              req.status === "PENDING" &&
              req.entityType === "CHILD" &&
              req.action === "UPDATE"
          )
          .map((req) => extractChildIdFromRequest(req))
          .filter((childId): childId is string => Boolean(childId))
      );
    }, [pendingRequests]);

    const hasPendingFamilySituationRequest = useMemo(() => {
      if (!family?.id) return false;

      return pendingRequests.some((req) => {
        if (
          req.status !== "PENDING" ||
          req.entityType !== "FAMILY" ||
          req.action !== "UPDATE" ||
          String(req.entityId || "") !== String(family.id)
        ) {
          return false;
        }

        const payload = parseRequestPayload(req.payload);
        return !!payload?.familySituationChange;
      });
    }, [family, pendingRequests]);

  const buildTripFormDraft = useCallback(
    (req?: TripConsentRequest | null): TripConsentFormState => {
      const child = req?.childId
        ? children.find((c) => String(c.id) === String(req.childId))
        : undefined;
        const parent1 = [family?.parent1FirstName, family?.parent1LastName].filter(Boolean).join(" ").trim();
        const parent2 = [family?.parent2FirstName, family?.parent2LastName].filter(Boolean).join(" ").trim();
        const parentsName = [parent1, parent2].filter(Boolean).join(" & ") ||
          [parentData?.firstName, parentData?.lastName].filter(Boolean).join(" ").trim();

        const childName =
          req?.childName ||
          [child?.firstName, child?.lastName].filter(Boolean).join(" ").trim();

        const dateBirth = child?.dateBirth ? formatDateDMY(child.dateBirth) : "";

        const className =
          req?.classroomName ||
          (() => {
            const key = child?.id ? toChildKey(child.id) : null;
            const cls = key ? classroomsByChild[key]?.data : null;
            if (!cls) return "";
            if (Array.isArray(cls)) {
              const current = cls.find((c) => c.current || c.isCurrent) || cls[0];
              return current?.className || "";
            }
            return cls?.className || "";
          })();
        const phoneList = Array.isArray(req?.parentPhoneNumbers) ? req?.parentPhoneNumbers : [];
        const parentPhone1 =
          req?.parent1Phone ||
          phoneList[0] ||
          family?.parent1Phone ||
          family?.parent1Mobile ||
          parentData?.phone ||
          "";
        const parentPhone2 =
          req?.parent2Phone ||
          phoneList[1] ||
          family?.parent2Phone ||
          family?.parent2Mobile ||
          "";
        const parentsPhone =
          req?.parentPhones ||
          [parentPhone1 || "-", parentPhone2 || "-"].join("; ") ||
          parentPhone1 ||
          "";

        return {
          childName: childName || "",
          dateBirth: dateBirth || "",
          className: className || "",
          parentNames: parentsName || "",
          parentPhones: parentsPhone || "",
          emergencyContact: "",
          emergencyPhone: "",
          doctorName: "",
          doctorPhone: "",
          allergies: "",
          medicalNotes: "",
          swimmingDiploma: null,
          tripConsent: false,
          saveToHealthProfile: false,
        };
      },
      [children, family, parentData, classroomsByChild]
    );

  const refreshTripFormDraft = useCallback(
    (req: TripConsentRequest) => {
      setTripFormDraft((prev) => {
        const base = buildTripFormDraft(req);
        // New request or no previous draft: use fresh base
          if (!prev || String(req.id) !== String(activeTripRequestId ?? req.id)) {
            return base;
          }

        return {
          ...prev,
          childName: base.childName,
          dateBirth: base.dateBirth,
          className: base.className,
          parentNames: base.parentNames,
          parentPhones: prev.parentPhones || base.parentPhones,
        };
      });
    },
      [buildTripFormDraft, activeTripRequestId]
    );

  const getChildName = useCallback(
    (childId?: EntityId | null) => {
      if (!childId) return null;
      const match = children.find((c) => String(c.id) === String(childId));
      if (match) return [match.firstName, match.lastName].filter(Boolean).join(" ").trim() || null;
      return null;
    },
    [children]
  );

  const getOutingKey = useCallback((item: any) => {
    return String(item?.childId ?? item?.targetUrl ?? item?.id ?? item?.label ?? "");
  }, []);

    const fetchParentActions = useCallback(async () => {
      try {
        setTripRequestsLoading(true);
        const { data } = await http.get<ParentAction[]>("/parent-actions/mine");
        const list = Array.isArray(data) ? data : [];
        setParentActions(list);
        const outing = list
          .filter((item) => (item.type || "").toUpperCase() === "OUTING_INFO")
          .map((item, idx) => ({
            id: item.childId || idx,
            childId: item.childId || idx,
            classroomName: item.classroomName || null,
            childName: getChildName(item.childId) || item.label || null,
            expiresAt: item.expiresAt || null,
            label: item.label || null,
            targetUrl: item.targetUrl || null,
          }));
        setTripRequests(outing);

        if (activeTripRequestId) {
          const stillExists = outing.some((req) => String(req.id) === String(activeTripRequestId));
          if (!stillExists) {
            setActiveTripRequestId(null);
            setTripFormDraft(null);
          }
        }
      } catch (err) {
        console.error("Failed to load parent actions:", err);
      } finally {
        setTripRequestsLoading(false);
      }
    }, [activeTripRequestId, getChildName]);

  // Fill in the trip form from the health profile the parents keep; only fields that are still empty.
  const activeTripChildId = activeTripRequest?.childId ?? null;
  useEffect(() => {
    if (!activeTripChildId) return;
    let cancelled = false;
    fetchChildHealthProfile(activeTripChildId)
      .then((profile) => {
        if (cancelled || !profile) return;
        const fill = (current: string, value?: string | null) => (current ? current : value || "");
        setTripFormDraft((prev) =>
          prev
            ? {
                ...prev,
                doctorName: fill(prev.doctorName, profile.effective?.doctorName),
                doctorPhone: fill(prev.doctorPhone, profile.effective?.doctorPhone),
                emergencyContact: fill(prev.emergencyContact, profile.effective?.emergencyContact),
                emergencyPhone: fill(prev.emergencyPhone, profile.effective?.emergencyPhone),
                allergies: fill(prev.allergies, profile.allergies),
                medicalNotes: fill(prev.medicalNotes, profile.medicalNotes),
                swimmingDiploma:
                  prev.swimmingDiploma === true || prev.swimmingDiploma === false
                    ? prev.swimmingDiploma
                    : profile.swimmingDiploma ?? null,
                saveToHealthProfile: prev.saveToHealthProfile || !!profile.exists,
              }
            : prev
        );
      })
      .catch(() => {
        // best effort: the form also works without a saved health profile
      });
    return () => {
      cancelled = true;
    };
  }, [activeTripChildId]);

  useEffect(() => {
    if (!activeTripRequest) return;
    refreshTripFormDraft(activeTripRequest);
    if (activeTripRequest.childId) {
      loadClassroomsForChild(activeTripRequest.childId);
    }
  }, [activeTripRequest, refreshTripFormDraft, loadClassroomsForChild]);

  const fetchParentPhonesForChild = useCallback(
    async (childId?: EntityId | null) => {
      if (!childId) return;
      try {
        const fetchFamily = async (familyId: EntityId) => {
          const { data } = await http.get(`/families/${familyId}`);
          return data;
        };

        let familyPayload: any = null;

        if (family?.id) {
          familyPayload = await fetchFamily(family.id);
        } else {
          // No family in state: resolve via child-family mapping
          const { data: cfData } = await http.get(`/child-family/by-child/${childId}`);
          const mapping = Array.isArray(cfData) ? cfData[0] : cfData;
          const familyId = mapping?.familyId || mapping?.family?.id;
          if (familyId) {
            familyPayload = await fetchFamily(familyId);
          }
        }

        if (!familyPayload) return;

        const p1 =
          familyPayload?.parent1Phone ||
          familyPayload?.parent1Mobile ||
          familyPayload?.parent1?.phone ||
          familyPayload?.parent1?.mobile ||
          "";
        const p2 =
          familyPayload?.parent2Phone ||
          familyPayload?.parent2Mobile ||
          familyPayload?.parent2?.phone ||
          familyPayload?.parent2?.mobile ||
          "";
        const combined = [p1 || "-", p2 || "-"].join("; ");
        setTripFormDraft((prev) =>
          prev
            ? {
                ...prev,
                parentPhones: combined || prev.parentPhones || "-",
              }
            : prev
        );
      } catch (err) {
        // best effort; ignore failures
      }
    },
    [family?.id, http]
  );

  const fetchOutingStatusForChild = useCallback(
    async (childId?: EntityId | null, options?: { updateDraft?: boolean }) => {
      if (!childId) return;
      const updateDraft = options?.updateDraft ?? false;
      try {
        const { data } = await http.get(`/outing-info/children/${childId}/status`);
        const status = Array.isArray(data) ? data[0] : data;
        if (!status) return;
        if (updateDraft) {
          setTripFormDraft((prev) =>
            prev
              ? {
                  ...prev,
                  parentNames: status.parentNames || prev.parentNames,
                  parentPhones: status.parentPhones || prev.parentPhones,
                  emergencyContact: status.emergencyContact ?? prev.emergencyContact,
                  emergencyPhone: status.emergencyPhone ?? prev.emergencyPhone,
                  doctorName: status.doctorName ?? prev.doctorName,
                  doctorPhone: status.doctorPhone ?? prev.doctorPhone,
                  allergies: status.allergies ?? prev.allergies,
                  medicalNotes: status.medicalNotes ?? prev.medicalNotes,
                  swimmingDiploma:
                    status.swimmingDiploma === true || status.swimmingDiploma === false
                      ? status.swimmingDiploma
                      : prev.swimmingDiploma,
                }
              : prev
          );
        }
        setTripRequests((prev) =>
          prev.map((req) =>
            String(req.childId) === String(childId)
              ? {
                  ...req,
                  outingName: status.outingName || req.outingName,
                  startsAt: status.startsAt || req.startsAt,
                  expiresAt: status.expiresAt || req.expiresAt,
          status: status.status || req.status || null,
          parent1Id: status.parent1Id || req.parent1Id || family?.parent1Id || null,
          parent2Id: status.parent2Id || req.parent2Id || family?.parent2Id || null,
                  parent1ConsentedAt: status.parent1ConsentedAt || null,
                  parent2ConsentedAt: status.parent2ConsentedAt || null,
                  consentsGiven: status.consentsGiven ?? req.consentsGiven,
                  consentsRequired: status.consentsRequired ?? req.consentsRequired,
                  requiresDualConsent: status.requiresDualConsent ?? req.requiresDualConsent,
                }
              : req
          )
        );
      } catch (err) {
        // ignore if no active outing (404)
      }
    },
    [family?.parent1Id, family?.parent2Id, http]
  );

  useEffect(() => {
    if (activeTripRequest?.childId) {
      fetchParentPhonesForChild(activeTripRequest.childId);
      fetchOutingStatusForChild(activeTripRequest.childId, { updateDraft: true });
    }
  }, [activeTripRequest?.childId, fetchOutingStatusForChild, fetchParentPhonesForChild]);

    useEffect(() => {
      if (!activeTripRequest) return;
      refreshTripFormDraft(activeTripRequest);
    }, [classroomsByChild, activeTripRequest, refreshTripFormDraft]);

    useEffect(() => {
      if (!bearer) return;
      fetchParentActions();
    }, [bearer, fetchParentActions]);

  useEffect(() => {
    if (!parentActions.length) return;
    const dedupe = (list: any[]) => {
      const map = new Map<string, any>();
      list.forEach((item) => {
        const keyParts = [
          item.childId || item.id || "",
          item.outingId || item.targetUrl || item.label || "",
        ];
        const key = keyParts.filter(Boolean).join("|");
        if (!key) return;
        map.set(key, { ...(map.get(key) || {}), ...item });
      });
      return Array.from(map.values());
    };
    const outing = parentActions
      .filter((item) => (item.type || "").toUpperCase() === "OUTING_INFO")
      .filter((item) => !dismissedOutingChildIds.has(getOutingKey(item)))
      .map((item, idx) => ({
        id: item.childId || idx,
        childId: item.childId || idx,
        classroomName: item.classroomName || null,
        childName: getChildName(item.childId) || item.label || null,
        expiresAt: item.expiresAt || null,
        status: item.status || null,
        label: item.label || null,
        targetUrl: item.targetUrl || null,
      }));
    setTripRequests((prev) => dedupe([...prev, ...outing]));
    outing.forEach((req) => fetchOutingStatusForChild(req.childId, { updateDraft: false }));
  }, [children, parentActions, getChildName, fetchOutingStatusForChild, dismissedOutingChildIds, getOutingKey]);


  const apiBase = String(config?.apiBaseUrl || "").replace(/\/+$/, "");
  const schoolCalendarLink = apiBase ? `${apiBase}/calendar/school.ics` : "/api/v1/calendar/school.ics";

  const getClassroomCalendarId = (assignment: ClassroomAssignment) =>
    assignment?.classroomUuid ??
    assignment?.classroomId ??
    assignment?.classroom?.uuid ??
    assignment?.classroom?.id ??
    assignment?.uuid ??
    assignment?.id ??
    "";

  const getClassroomIds = (classroom?: ClassroomAssignment | null) =>
    [
      classroom?.id,
      classroom?.uuid,
      classroom?.classroomId,
      classroom?.classroomUuid,
      classroom?.classroom?.id,
      classroom?.classroom?.uuid,
    ]
      .filter((value) => value !== undefined && value !== null && String(value).trim())
      .map((value) => String(value));

  const getCalendarClassroomIds = (event: ParentCalendarEvent) => {
    const ids = new Set<string>();

    if (Array.isArray(event.classroomUuids)) {
      event.classroomUuids.forEach((id) => {
        if (id !== undefined && id !== null && String(id).trim()) ids.add(String(id));
      });
    }

    if (Array.isArray(event.classroomIds)) {
      event.classroomIds.forEach((id) => {
        if (id !== undefined && id !== null && String(id).trim()) ids.add(String(id));
      });
    }

    if (Array.isArray(event.classrooms)) {
      event.classrooms.forEach((classroom) => {
        getClassroomIds(classroom).forEach((id) => ids.add(id));
      });
    }

    return Array.from(ids);
  };

  const classroomLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    const addClassroom = (classroom?: ClassroomAssignment | null) => {
      const name = getClassroomCalendarLabel(classroom);
      if (!name) return;
      getClassroomIds(classroom).forEach((id) => {
        lookup[id] = name;
      });
    };

    (Object.values(classroomsByChild) as ClassroomState[]).forEach((state) => {
      const rows = Array.isArray(state?.data) ? state.data : state?.data ? [state.data] : [];
      rows.forEach(addClassroom);
    });

    return lookup;
  }, [classroomsByChild]);

  const classCalendarLinks = useMemo(() => {
    const byClass = new Map<string, { id: string; label: string }>();

    (Object.values(classroomsByChild) as ClassroomState[]).forEach((state) => {
      const rows = Array.isArray(state?.data) ? state.data : state?.data ? [state.data] : [];

      rows.forEach((assignment) => {
        const id = String(getClassroomCalendarId(assignment));
        if (!id) return;

        const label =
          getClassroomCalendarLabel(assignment) ||
          t("parentPortal.calendar.class_agenda", { defaultValue: "Class calendar" });

        byClass.set(id, { id, label });
      });
    });

    return Array.from(byClass.values()).map((item) => ({
      ...item,
      url: apiBase
        ? `${apiBase}/calendar/classrooms/${encodeURIComponent(item.id)}.ics`
        : `/api/v1/calendar/classrooms/${encodeURIComponent(item.id)}.ics`,
    }));
  }, [apiBase, classroomsByChild, t]);

  const calendarDatePart = (value?: string | number[] | null) => {
    if (!value) return "";
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
  };

  const calendarTimePart = (value?: string | number[] | null) => {
    if (!value) return "";
    if (Array.isArray(value) && value.length >= 5) {
      return `${String(value[3]).padStart(2, "0")}:${String(value[4]).padStart(2, "0")}`;
    }
    const raw = String(value).trim();
    const match = raw.match(/T(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
    return "";
  };

  const calendarTimeValue = (value?: string | number[] | null) => {
    if (!value) return Number.MAX_SAFE_INTEGER;
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = value;
      return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)).getTime();
    }
    const time = new Date(String(value)).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  };

  const formatCalendarDate = (value?: string | number[] | null) => {
    const datePart = calendarDatePart(value);
    if (!datePart) return "-";
    const [year, month, day] = datePart.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(dateLocale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatCalendarTime = (event: ParentCalendarEvent) => {
    if (event.allDay) return t("parentPortal.calendar.all_day", { defaultValue: "All day" });
    const start = calendarTimePart(event.startDate);
    const end = calendarTimePart(event.endDate);
    if (start && end) return `${start} - ${end}`;
    return start || end || "";
  };

  const getCalendarClassNames = (event: ParentCalendarEvent) => {
    if (Array.isArray(event.classroomNames) && event.classroomNames.length) {
      return event.classroomNames
        .map((name) => translateClassName(String(name)) || String(name))
        .filter(Boolean)
        .join(", ");
    }
    if (Array.isArray(event.classrooms) && event.classrooms.length) {
      return event.classrooms
        .map((classroom) => getClassroomCalendarLabel(classroom as ClassroomAssignment))
        .filter(Boolean)
        .join(", ");
    }
    const names = getCalendarClassroomIds(event)
      .map((id) => classroomLookup[id])
      .filter(Boolean);
    if (names.length) return Array.from(new Set(names)).join(", ");
    return "";
  };

  const upcomingCalendarEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...calendarEvents]
      .filter((event) => calendarTimeValue(event.endDate || event.startDate) >= today.getTime())
      .sort((a, b) => calendarTimeValue(a.startDate) - calendarTimeValue(b.startDate));
  }, [calendarEvents]);

  const nextCalendarEvent = upcomingCalendarEvents[0] || null;

  const groupedCalendarEvents = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(dateLocale, { month: "long", year: "numeric" });
    return [...calendarEvents]
      .sort((a, b) => calendarTimeValue(a.startDate) - calendarTimeValue(b.startDate))
      .reduce<Array<{ key: string; label: string; events: ParentCalendarEvent[] }>>((acc, event) => {
        const datePart = calendarDatePart(event.startDate);
        const [year, month, day] = datePart.split("-");
        const date = year && month && day ? new Date(Number(year), Number(month) - 1, Number(day)) : null;
        const key = date ? `${date.getFullYear()}-${date.getMonth()}` : "unknown";
        const label = date ? formatter.format(date) : t("parentPortal.calendar.no_date", { defaultValue: "No date" });
        let group = acc.find((item) => item.key === key);
        if (!group) {
          group = { key, label, events: [] };
          acc.push(group);
        }
        group.events.push(event);
        return acc;
      }, []);
  }, [calendarEvents, dateLocale, t]);

  const copyCalendarUrl = async (url: string, key: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCalendarCopied(key);
      window.setTimeout(() => setCalendarCopied(null), 1800);
    } catch {
      setModal({
        show: true,
        type: "error",
        title: t("common.modal.error", { defaultValue: "Error" }),
        message: t("parentPortal.calendar.copy_failed", { defaultValue: "Copying failed." }),
      });
    }
  };

  const loadCalendarEvents = useCallback(async () => {
    if (!authenticated || !bearer) return;
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      const dateParam = (date: Date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const today = new Date();
      const schoolYearStartYear = today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1;
      const fromDate = new Date(schoolYearStartYear, 7, 1);
      const toDate = new Date(schoolYearStartYear + 1, 6, 31);
      const from = dateParam(fromDate);
      const to = dateParam(toDate);
      const { data } = await http.get<ParentCalendarEvent[]>("/parent-portal/calendar/events", {
        params: {
          from: `${from}T00:00:00`,
          to: `${to}T23:59:59`,
          published: true,
        },
      });
      const nextEvents = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.content)
          ? (data as any).content
          : Array.isArray((data as any)?.items)
            ? (data as any).items
            : Array.isArray((data as any)?.events)
              ? (data as any).events
              : [];
      setCalendarEvents(nextEvents);
    } catch (err) {
      console.error("Failed to load parent calendar events:", err);
      setCalendarEvents([]);
      setCalendarError(
        t("parentPortal.calendar.load_failed", {
          defaultValue: "Calendar could not be loaded. Please try again later.",
        })
      );
    } finally {
      setCalendarLoading(false);
    }
  }, [authenticated, bearer, t]);

  useEffect(() => {
    if (!authenticated || !bearer) return;
    loadCalendarEvents();
  }, [authenticated, bearer, loadCalendarEvents]);

    if (families.length > 1 && !family) {
        return (
            <PpFamilySelector
                families={families}
                onSelect={(fam) => {
                    setFamily(fam);
                    setFamilyDraft(fam);
                }}
            />
        );
    }

    if (!authenticated) return null;

    const childIdSet = new Set(children.map((c) => String(c.id)));
    const pendingChildCreate = pendingChildren
      .filter((c) => c.familyId === family?.id)
      .filter((c) => {
        if (!c.action || c.action === "CREATE") {
          return !c.entityId && !childIdSet.has(String(c.id));
        }
        return false;
      })
      .map((c) => ({
        ...c,
        pending: true,
      }));

    const visibleChildren = [...children, ...pendingChildCreate].filter((c) => !isDeceased(c));

    const childrenMissingBsn = visibleChildren.filter((child) => {
      const isPendingCreation =
        !!child.pending && (!child.entityId || String(child.id).startsWith("pending-"));
      if (isPendingCreation) return false;
      const hasBsn = typeof child.bsn === "string" && child.bsn.trim().length > 0;
      const hasPendingBsn = typeof child.pendingBsn === "string" && child.pendingBsn.trim().length > 0;
      return !hasBsn && !hasPendingBsn;
    });
    const snoozedBsnCount = childrenMissingBsn.filter((child) => isBsnSnoozed(child.id)).length;
    const actionableChildrenMissingBsn = childrenMissingBsn.filter(
      (child) => !pendingChildUpdateSet.has(String(child.id)) && !isBsnSnoozed(child.id)
    );

    if (loading) return <Spinner theme="parent" />;
    if (error) {
      const normalizedError =
        typeof error === "object" && error !== null ? (error as { message?: string; status?: number }) : null;
      return (
        <PPErrorPage
          message={typeof error === "string" ? error : normalizedError?.message}
          status={normalizedError?.status}
          onRetry={() => window.location.reload()}
        />
      );
    }

    const pageTitle = family?.familyName || parentData?.lastName || t("parentPortal.loading_title");
    const mobileFamilyName = family?.familyName || parentData?.lastName || "";
    const mobileSchoolName =
      (parentData?.schoolSettings?.schoolName as string) ||
      (parentData?.schoolSettings?.name as string) ||
      (parentData?.schoolName as string) ||
      (parentData?.school as any)?.name ||
      "de Werfklas";
    const activeTabItem = routableTabItems.find((tab) => tab.key === activeTab);
    const combinedTitle = activeTabItem ? `${pageTitle} - ${activeTabItem.label}` : pageTitle;
    const newsletterStatus = parentData?.newsletterStatus || "INELIGIBLE";
    const isNewsletterEligible = newsletterStatus !== "INELIGIBLE";
    const newsletterFirstSubscribed = formatDateDMY(parentData?.newsletterFirstSubscribedAt);
    const pendingRequestCount = pendingRequests.filter((req) => req.status === "PENDING").length;
    const openParentMeetingCount = parentMeetingInvitations.filter((invitation) =>
      ["INVITED", "RESERVED"].includes((invitation.status || "").toUpperCase())
    ).length;
    const missingBsnCount = actionableChildrenMissingBsn.length;
    const openTripRequests = tripRequests.filter(
      (req) =>
        (req.status || "PENDING").toUpperCase() === "PENDING" &&
        !dismissedOutingChildIds.has(getOutingKey(req))
    );
    const pendingTripRequestCount = openTripRequests.length;
    const visibleChildrenById = new Map(
      visibleChildren.map((child) => [String(child.id), child])
    );
    const privacyActions = parentActions
      .filter((action) => {
        if (!isPrivacyPreferencesAction(action)) return false;
        const status = (action.status || "").toUpperCase();
        return !status || status === "MISSING" || status === "EXPIRED";
      })
      .reduce<Array<{ id: string; child: Child; childName: string; action: ParentAction }>>((acc, action) => {
        if (action.childId == null) return acc;

        const key = toChildKey(action.childId);
        if (acc.some((item) => item.id === `privacy-${key}`)) return acc;

        const child = visibleChildrenById.get(key);
        if (!child) return acc;

        const childName =
          [child.firstName, child.lastName].filter(Boolean).join(" ") ||
          action.label ||
          t("common.unknown_child", { defaultValue: "Child" });

        acc.push({
          id: `privacy-${key}`,
          child,
          childName,
          action,
        });
        return acc;
      }, []);
    const privacyActionCount = privacyActions.length;
    const healthActions = parentActions
      .filter((action) => (action.type || "").toUpperCase() === HEALTH_PROFILE_REVIEW_ACTION_TYPE)
      .reduce<Array<{ id: string; child: Child; childName: string; action: ParentAction }>>((acc, action) => {
        if (action.childId == null) return acc;
        const key = toChildKey(action.childId);
        if (acc.some((item) => item.id === `health-${key}`)) return acc;
        const child = visibleChildrenById.get(key);
        if (!child) return acc;
        const childName =
          [child.firstName, child.lastName].filter(Boolean).join(" ") ||
          action.label ||
          t("common.unknown_child", { defaultValue: "Child" });
        acc.push({ id: `health-${key}`, child, childName, action });
        return acc;
      }, []);
    const privacyReviewItems = privacyActions.filter(({ action }) => needsPrivacyReview(action.status));
    const totalActions =
      missingBsnCount +
      pendingTripRequestCount +
      pendingRequestCount +
      openParentMeetingCount +
      privacyActionCount +
      healthActions.length;
    const actionSubtexts: string[] = [];
    if (missingBsnCount) {
      actionSubtexts.push(
        t("parentPortal.summary.actions_missing_bsn", {
          defaultValue: "Update BSN for {{count}} child profile(s)",
          count: missingBsnCount,
        })
      );
    }
    if (pendingTripRequestCount) {
      actionSubtexts.push(
        t("parentPortal.summary.actions_trip_forms", {
          defaultValue: "Trip consent: {{count}} open",
          count: pendingTripRequestCount,
        })
      );
    }
    if (openParentMeetingCount) {
      actionSubtexts.push(
        t("parentPortal.summary.actions_parent_meetings", {
          defaultValue: "Parent meeting: {{count}} open",
          count: openParentMeetingCount,
        })
      );
    }
    if (privacyActionCount) {
      actionSubtexts.push(
        t("parentPortal.summary.actions_privacy", {
          defaultValue: "Privacy preferences: {{count}} open",
          count: privacyActionCount,
        })
      );
    }
    if (pendingRequestCount) {
      actionSubtexts.push(
        t("parentPortal.summary.actions_pending_requests", {
          defaultValue: "Request: {{count}} pending",
          count: pendingRequestCount,
        })
      );
    }
    const handleFixChildBsn = (child: Child) => {
      if (!child?.id) return;
      setBsnActionChildId(child.id);
      setActiveTab("family");
    };

    const handleOpenPrivacyAction = (child: Child) => {
      if (!child?.id) return;
      setPrivacyActionChildId(child.id);
      setActiveTab("family");
    };

    const handleOpenHealthAction = (child: Child) => {
      if (!child?.id) return;
      // The review is done by confirming or saving the profile; drop the action locally right away.
      setParentActions((prev) =>
        prev.filter(
          (a) =>
            (a.type || "").toUpperCase() !== HEALTH_PROFILE_REVIEW_ACTION_TYPE ||
            String(a.childId) !== String(child.id)
        )
      );
      setHealthActionChildId(child.id);
      setActiveTab("family");
    };

    const findChildFromRequest = (req: MutationRequest) => {
      const parsed = parseRequestPayload(req.payload);
      const candidateIds = [
        req.entityId,
        parsed?.childId,
        parsed?.payload?.childId,
        parsed?.id,
        parsed?.payload?.id,
      ].filter(Boolean);

      for (const candidate of candidateIds) {
        const match =
          children.find((child) => String(child.id) === String(candidate)) ||
          pendingChildren.find((child) => {
            if (child.entityId) return String(child.entityId) === String(candidate);
            return String(child.id) === String(candidate);
          });
        if (match) return match;
      }

      const pendingByRequest = pendingChildren.find((child) => String(child.id) === String(req.id));
      if (pendingByRequest) return pendingByRequest;

      if (parsed?.firstName) {
        const first = parsed.firstName?.toLowerCase();
        const last = parsed.lastName?.toLowerCase();
        const match =
          children.find(
            (child) =>
              child.firstName?.toLowerCase() === first && child.lastName?.toLowerCase() === last
          ) ||
          pendingChildren.find(
            (child) =>
              child.firstName?.toLowerCase() === first && child.lastName?.toLowerCase() === last
          );
        if (match) return match;
      }

      return null;
    };

    const handleRequestNavigate = (req: MutationRequest) => {
      if (req.entityType === "CHILD") {
        setActiveTab("children");
        const child = findChildFromRequest(req);
        if (child) {
          setExpandedChildId(child.id);
        }
        return;
      }
      if (req.entityType === "FAMILY") {
        setActiveTab("family");
        return;
      }
      if (req.entityType === "BANK_ACCOUNT") {
        setActiveTab("finance");
        // Laat niet opnieuw indienen als er al een IBAN-aanvraag open staat
        const isPending = (req.status || "").toUpperCase() === "PENDING";
        if (hasPendingBankMutation || isPending) {
          setEditBankMode(false);
          setModal({
            show: true,
            type: "error",
            title: t("common.modal.error"),
            message: t("parentPortal.bank_change_pending", {
              defaultValue: "Your new bank account number has been saved and is ready for administrative processing.",
            }),
          });
        } else {
          setEditBankMode(true);
        }
        return;
      }
      if (req.entityType === "CHILD" && req.action === "WITHDRAW") {
        // navigate to alumni preferences page (new)
        const parsedPayload = parseRequestPayload(req.payload);
        const childId =
          req.entityId ||
          (parsedPayload && typeof parsedPayload === "object" ? parsedPayload.childId : null) ||
          null;
        navigate(childId ? `/portal/alumni-preferences?childId=${childId}` : "/portal/alumni-preferences");
        return;
      }
      setActiveTab("info");
    };

    const handleOpenTripRequest = (req: TripConsentRequest) => {
      setActionsView("open");
      setTripFormError(null);
      setActiveTripRequestId(req.id);
      setTripFormDraft(buildTripFormDraft(req));
    };

    const handleOpenParentMeetingInvitation = (invitation: ParentMeetingInvitation) => {
      setActionsView("open");
      setActiveParentMeetingInvitationId(invitation.id);
    };

    const handleParentMeetingInvitationConfirmed = () => {
      fetchParentMeetingInvitations();
    };

    const handleSubmitTripForm = async (
      requestOverride?: TripConsentRequest | null,
      draftOverride?: TripConsentFormState | null
    ) => {
      const reqToUse = requestOverride || activeTripRequest;
      const draftToUse = draftOverride || tripFormDraft;
      if (!reqToUse || !draftToUse) return;
      setTripFormSaving(true);
      setTripFormError(null);

      try {
        await http.post(`/outing-info/children/${reqToUse.childId}`, {
          parentNames: draftToUse.parentNames,
          parentPhones: draftToUse.parentPhones,
          emergencyContact: draftToUse.emergencyContact,
          emergencyPhone: draftToUse.emergencyPhone,
          doctorName: draftToUse.doctorName,
          doctorPhone: draftToUse.doctorPhone,
          allergies: draftToUse.allergies,
          medicalNotes: draftToUse.medicalNotes,
          swimmingDiploma: draftToUse.swimmingDiploma,
          tripConsent: draftToUse.tripConsent,
          saveToHealthProfile: !!draftToUse.saveToHealthProfile,
        });

        setModal({
          show: true,
          type: "success",
          title: t("common.modal.success"),
          message: t("parentPortal.trip_consent.saved", { defaultValue: "Form submitted. Thank you!" }),
        });

        // verwijder lokaal item en herlaad uit backend zodat de kaart verdwijnt
        const dismissedKey = getOutingKey(reqToUse);
        setTripRequests((prev) =>
          prev.filter((req) => getOutingKey(req) !== dismissedKey)
        );
        setParentActions((prev) =>
          prev.filter(
            (a) =>
              (a.type || "").toUpperCase() !== "OUTING_INFO" ||
              getOutingKey(a) !== dismissedKey
          )
        );
        setDismissedOutingChildIds((prev) => {
          const next = new Set(prev);
          next.add(dismissedKey);
          return next;
        });
        setActiveTripRequestId(null);
        setTripFormDraft(null);
      } catch (err) {
        console.error("Failed to submit trip consent:", err);
        setTripFormError(
          t("parentPortal.trip_consent.error", {
            defaultValue: "Saving failed. Please try again or contact the teacher.",
          })
        );
      } finally {
        setTripFormSaving(false);
      }
    };
 

    const renderTabContent = (
      InfoComponent: React.ComponentType<any>,
      ActionsComponent: React.ComponentType<any> = ActionsTab,
      FamilyComponent: React.ComponentType<any> = FamilyTab,
      DirectoryComponent: React.ComponentType<any> = DirectoryTab,
      CalendarComponent: React.ComponentType<any> = CalendarTab,
      includeOverview = false
    ) => (
      <>
            {includeOverview && activeTab === "overview" && (
              <ParentPortalDashboard
                t={t}
                parentData={parentData}
                family={family}
                visibleChildren={visibleChildren}
                nextCalendarEvent={nextCalendarEvent}
                formatCalendarDate={formatCalendarDate}
                formatCalendarTime={formatCalendarTime}
                totalActions={totalActions}
                actionSubtexts={actionSubtexts}
                onOpenCalendar={() => setActiveTab("calendar")}
                onOpenActions={() => setActionsView("open")}
                onOpenFamily={() => setActiveTab("family")}
                onOpenDirectory={(payload: { search?: string } | string | undefined) => {
                  const search =
                    typeof payload === "string"
                      ? payload.trim()
                      : String(payload?.search || "").trim();

                  setDirQ(search);
                  setDirPage(0);
                  setDirData(null);
                  setActiveTab("directory");

                  if (dirTabBooted && search) {
                    void loadDirectory(0, search, dirMode);
                  }
                }}
                editMode={editMode}
                setEditMode={setEditMode}
                form={form}
                handleChange={handleChange}
                handleSave={handleSave}
                newsletterStatus={newsletterStatus}
                newsletterFirstSubscribed={newsletterFirstSubscribed}
                isNewsletterEligible={isNewsletterEligible}
              />
            )}

            {activeTab === "info" && (
              <InfoComponent
                t={t}
                parentData={parentData}
                editMode={editMode}
                setEditMode={setEditMode}
                form={form}
                handleChange={handleChange}
                handleSave={handleSave}
                newsletterStatus={newsletterStatus}
                newsletterFirstSubscribed={newsletterFirstSubscribed}
                isNewsletterEligible={isNewsletterEligible}
                nextCalendarEvent={nextCalendarEvent}
                formatCalendarDate={formatCalendarDate}
                formatCalendarTime={formatCalendarTime}
                getCalendarClassNames={getCalendarClassNames}
                upcomingCalendarEvents={upcomingCalendarEvents}
                actionSubtexts={actionSubtexts}
                totalActions={totalActions}
                onOpenActions={() => setActionsView("open")}
                onOpenCalendar={() => setActiveTab("calendar")}
                onOpenFamily={() => setActiveTab("family")}
                editBankMode={editBankMode}
                setEditBankMode={setEditBankMode}
                bankForm={bankForm}
                setBankForm={setBankForm}
                handleSubmitBankChange={handleSubmitBankChange}
                bankAccount={bankAccount}
                hasPendingBankMutation={hasPendingBankMutation}
              />
            )}

            {actionsTabVisited && (
              <div className="space-y-5" hidden={activeTab !== "actions"}>
                <ParentActionsViewCache
                  activeView={actionsView}
                  schoolTasks={<ParentSchoolTasks embedded />}
                  openActions={<>
                  <ActionsComponent
                  t={t}
                  missingChildren={actionableChildrenMissingBsn}
                  onFixChild={handleFixChildBsn}
                  alumniChildren={withdrawnChildren}
                  onAlumniAction={(childId) => {
                    navigate(
                      childId
                        ? `/portal/alumni-preferences?childId=${childId}`
                        : "/portal/alumni-preferences"
                    );
                  }}
                  onSnoozeChild={handleSnoozeBsn}
                  onDismissChild={handleDismissBsn}
                  snoozedCount={snoozedBsnCount}
                  onClearSnoozes={handleClearBsnSnoozes}
                  outingActions={openTripRequests}
                  onOpenOuting={handleOpenTripRequest}
                  privacyActions={privacyActions}
                  onOpenPrivacyAction={handleOpenPrivacyAction}
                  healthActions={healthActions}
                  onOpenHealthAction={handleOpenHealthAction}
                  activeTripRequest={activeTripRequestWithCurrentParent}
                  tripFormDraft={tripFormDraft}
                  onTripFormChange={(field, value) =>
                    setTripFormDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            [field]: value as TripConsentFormState[keyof TripConsentFormState],
                          }
                        : prev
                    )
                  }
                  onTripFormSubmit={handleSubmitTripForm}
                  onCloseTripRequest={() => setActiveTripRequestId(null)}
                  tripFormSaving={tripFormSaving}
                  tripFormError={tripFormError}
                  currentParentId={parentData?.id || null}
                  requests={pendingRequests}
                  parentMeetingInvitations={parentMeetingInvitations}
                  activeParentMeetingInvitation={activeParentMeetingInvitation}
                  onOpenParentMeetingInvitation={handleOpenParentMeetingInvitation}
                  onCloseParentMeetingInvitation={() => setActiveParentMeetingInvitationId(null)}
                  onParentMeetingInvitationConfirmed={handleParentMeetingInvitationConfirmed}
                  childrenList={children}
                  pendingChildren={pendingChildren}
                  families={families}
                  parentData={parentData}
                  onSelectRequest={handleRequestNavigate}
                  childInfoEdit={childInfoEdit}
                  startChildInfoEdit={startChildInfoEdit}
                  updateChildInfoDraft={updateChildInfoDraft}
                  cancelChildInfoEdit={cancelChildInfoEdit}
                  submitChildInfoEdit={submitChildInfoEdit}
                  classroomsByChild={classroomsByChild}
                  pendingChildUpdateSet={pendingChildUpdateSet}
                  />

                {snoozedBsnCount > 0 && (
                  <div className="rounded-2xl border border-amber-200/70 bg-amber-50/70 px-4 py-3 text-center text-sm text-amber-800 shadow-sm dark:border-amber-800/40 dark:bg-amber-900/40 dark:text-amber-100">
                    {t("parentPortal.actions.snoozed_hint", {
                      defaultValue: "{{count}} action(s) snoozed/hidden",
                      count: snoozedBsnCount,
                    })}
                    <button
                      type="button"
                      onClick={handleClearBsnSnoozes}
                      className="ml-3 inline-flex items-center rounded-full border border-amber-300 bg-white/80 px-3 py-1 text-xs font-semibold text-amber-800 transition hover:bg-white dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-50"
                    >
                      {t("parentPortal.actions.snoozed_reset", { defaultValue: "Show them again" })}
                    </button>
                  </div>
                )}
                  </>}
                />
              </div>
            )}

            {activeTab === "calendar" && (
              <CalendarComponent
                t={t}
                calendarLoading={calendarLoading}
                calendarError={calendarError}
                groupedCalendarEvents={groupedCalendarEvents}
                loadCalendarEvents={loadCalendarEvents}
                formatCalendarDate={formatCalendarDate}
                formatCalendarTime={formatCalendarTime}
                getCalendarClassNames={getCalendarClassNames}
                classroomLookup={classroomLookup}
                schoolCalendarLink={schoolCalendarLink}
                classCalendarLinks={classCalendarLinks}
                copyCalendarUrl={copyCalendarUrl}
                calendarCopied={calendarCopied}
                schoolName={mobileSchoolName}
                familyName={mobileFamilyName}
              />
            )}

            {activeTab === "family" && (
              <FamilyComponent
                t={t}
                family={family}
                familyDraft={familyDraft}
                setFamilyDraft={setFamilyDraft}
                familyEditMode={familyEditMode}
                setFamilyEditMode={setFamilyEditMode}
                hasPendingFamilyUpdate={hasPendingFamilyUpdate}
                hasPendingFamilySituationRequest={hasPendingFamilySituationRequest}
                handleSaveFamilySituation={handleSaveFamilySituation}
                onSubmitFamilySituationRequest={handleSubmitFamilySituationRequest}
                onSubmitTeacherContactRequest={handleSubmitTeacherContactRequest}
                parentData={parentData}
                addMode={addMode}
                setAddMode={setAddMode}
                newChild={newChild}
                setNewChild={setNewChild}
                handleNewChildChange={handleNewChildChange}
                handleCreateChild={handleCreateChild}
                onAddChild={() => {
                  if (FamilyComponent === FamilyTab) {
                    setAddMode(true);
                    return;
                  }
                  setActiveTab("children");
                  setAddMode(true);
                }}
                onOpenChild={(child: Child) => {
                  if (FamilyComponent === FamilyTab) {
                    return;
                  }
                  setActiveTab("children");
                  setExpandedChildId(child.id);
                }}
                classroomsByChild={classroomsByChild}
                privacyByChild={privacyByChild}
                privacyActionChildId={privacyActionChildId}
                onPrivacyActionHandled={() => setPrivacyActionChildId(null)}
                bsnActionChildId={bsnActionChildId}
                onBsnActionHandled={() => setBsnActionChildId(null)}
                healthActionChildId={healthActionChildId}
                onHealthActionHandled={() => setHealthActionChildId(null)}
                loadPrivacyFor={loadPrivacyFor}
                loadClassroomsForChild={loadClassroomsForChild}
                startChildInfoEdit={startChildInfoEdit}
                childInfoEdit={childInfoEdit}
                updateChildInfoDraft={updateChildInfoDraft}
                cancelChildInfoEdit={cancelChildInfoEdit}
                submitChildInfoEdit={submitChildInfoEdit}
                savePrivacyFor={savePrivacyFor}
                pendingChildren={pendingChildren}
                pendingChildUpdateSet={pendingChildUpdateSet}
              />
            )}

            {activeTab === "children" && (
              <ChildrenTab
                t={t}
                visibleChildren={visibleChildren}
                expandedChildId={expandedChildId}
                setExpandedChildId={setExpandedChildId}
                privacyByChild={privacyByChild}
                loadPrivacyFor={loadPrivacyFor}
                togglePrivacyExpanded={togglePrivacyExpanded}
                startEditPrivacy={startEditPrivacy}
                cancelEditPrivacy={cancelEditPrivacy}
                savePrivacyFor={savePrivacyFor}
                loadClassroomsForChild={loadClassroomsForChild}
                classroomsByChild={classroomsByChild}
                childInfoExpanded={childInfoExpanded}
                toggleChildInfoExpanded={toggleChildInfoExpanded}
                startChildInfoEdit={startChildInfoEdit}
                childInfoEdit={childInfoEdit}
                updateChildInfoDraft={updateChildInfoDraft}
                cancelChildInfoEdit={cancelChildInfoEdit}
                submitChildInfoEdit={submitChildInfoEdit}
                addMode={addMode}
                setAddMode={setAddMode}
                newChild={newChild}
                setNewChild={setNewChild}
                handleNewChildChange={handleNewChildChange}
                handleCreateChild={handleCreateChild}
                isWithdrawn={isWithdrawn}
                isDeceased={isDeceased}
                pendingChildren={pendingChildren}
                pendingChildUpdateSet={pendingChildUpdateSet}
              />
            )}

            {activeTab === "finance" && (
              <FinanceTab
                t={t}
                editBankMode={editBankMode}
                setEditBankMode={setEditBankMode}
                bankForm={bankForm}
                setBankForm={setBankForm}
                handleSubmitBankChange={handleSubmitBankChange}
                bankAccount={bankAccount}
                hasPendingBankMutation={hasPendingBankMutation}
                currentYearFee={currentYearFee}
                formatCurrency={formatCurrency}
                billableChildren={billableChildren}
                familyId={family?.id}
              />
            )}

            {activeTab === "preferences" && <PreferencesTab t={t} parentData={parentData} />}

            {activeTab === "directory" && (
              <DirectoryComponent
                t={t}
                canViewDirectory={canViewDirectory}
                openDirSettings={openDirSettings}
                dirSettingsOpen={dirSettingsOpen}
                setDirSettingsOpen={setDirSettingsOpen}
                saveDirectorySettings={saveDirectorySettings}
                turnOffListing={turnOffListing}
                myDirErr={myDirErr}
                myDirSaving={myDirSaving}
                dirMode={dirMode}
                setDirMode={setDirMode}
                dirQ={dirQ}
                setDirQ={setDirQ}
                dirData={dirData}
                dirLoading={dirLoading}
                setDirData={setDirData}
                loadDirectory={loadDirectory}
                dirInputRef={dirInputRef}
                setDirPage={setDirPage}
                share={share}
                setShare={setShare}
                previewValues={previewValues}
              />
            )}

      </>
    );

    return (
      <>
        <ParentPortalResponsive
          tabItems={desktopTabItems}
          mobileTabItems={mobileTabItems}
          activeTab={activeTab}
          setActiveTab={(key) => {
            setProfileOpen(false);
            if (key === "actions") setActionsView("open");
            else setActiveTab(key);
          }}
          summaryCards={[]}
          familyName={mobileFamilyName}
          schoolName={mobileSchoolName}
          desktopChildren={renderTabContent(InfoTab, ActionsTab, FamilyTab, DirectoryTab, CalendarTab, true)}
          mobileChildren={
            profileOpen ? (
              <SettingsTab
                t={t}
                parentData={parentData}
                form={form}
                onChange={handleChange}
                onSave={handleSave}
                onBack={() => setProfileOpen(false)}
                onLogout={() => logout?.({ redirectUri: config.frontendUrl || window.location.origin })}
              />
            ) : (
              renderTabContent(MobileInfoTab, MobileActionsTab, MobileFamilyTab, MobileDirectoryTab, MobileCalendarTab)
            )
          }
          desktopTitle={combinedTitle}
          onOpenProfile={() => setProfileOpen(true)}
          onOpenSettings={() => setProfileOpen(true)}
          onOpenCalendarLink={() => {
            setProfileOpen(false);
            setActiveTab("calendar");
            window.setTimeout(() => {
              window.dispatchEvent(new CustomEvent("parentPortal:openCalendarSubscription"));
            }, 120);
          }}
          directorySearch={{
            value: dirQ,
            onChange: setDirQ,
            onSubmit: () => {
              setDirPage(0);
              void loadDirectory(0, dirQ, dirMode);
            },
            loading: dirLoading,
            placeholder: t("parentPortal.directory.search_placeholder", {
              defaultValue: "Search parents or children...",
            }),
          }}
          actionsView={actionsView}
          setActionsView={setActionsView}
          openActionsCount={totalActions}
          schoolTasksCount={schoolTasksAttentionCount}
          showSchoolTasks={canViewSchoolTasks}
          openActionsLabel={t("parentPortal.actions.open_actions", { defaultValue: "Open actions" })}
          schoolTasksLabel={t("schoolTasks.title", { defaultValue: "School tasks" })}
        />

        <Modal
          show={modal.show}
          type={modal.type}
          title={modal.title}
          message={modal.message}
          variant="parent"
          onClose={() => setModal({ show: false })}
        />
        {privacyReviewSnoozed === false &&
          shouldShowPrivacyReviewPopup(parentData?.schoolSettings, privacyReviewItems.map(({ action }) => action)) && (
          <PrivacyReviewModal
            title={String(parentData?.schoolSettings?.privacyReviewPopupTitle ?? "")}
            message={String(parentData?.schoolSettings?.privacyReviewPopupMessage ?? "")}
            items={privacyReviewItems.map(({ child, childName, action }) => ({
              childId: child.id,
              childName,
              status: action.status,
            }))}
            privacyByChild={privacyByChild}
            loadPrivacyFor={loadPrivacyFor}
            savePrivacyFor={savePrivacyFor}
            onRemindLater={() => {
              snoozePrivacyReview(parentData?.id, parentData?.schoolSettings?.privacyReviewSnoozeDays);
              setPrivacyReviewSnoozed(true);
            }}
          />
        )}
      </>
    );
};

export default ParentPortal;
